// REGLA 1: Default metrics estable - garantiza que estado nunca sea null
const DEFAULT_METRICS = {
  completedTurns: 0,
  maxTurns: 0,
  currentPhaseId: null,
  phaseHistoryLength: 0,
  progressPercentage: 0,
  aiTurnCount: 0
};

// Función de normalización única para métricas de sesión
function normalizeMetrics(data, prev = DEFAULT_METRICS) {
  // REGLA 1: Validar data antes de acceder - evita "can't access property on null"
  if (!data || typeof data !== 'object') {
    console.warn('[normalizeMetrics] Data inválido, retornando prev:', { data, prev });
    return prev;
  }

  const completedTurns =
    typeof data?.completedTurns === 'number' ? data.completedTurns :
    typeof data?.aiTurnCount === 'number' ? data.aiTurnCount :
    typeof data?.turnCount === 'number' ? data.turnCount :
    typeof data?.state?.completedTurns === 'number' ? data.state.completedTurns :
    typeof data?.state?.aiTurnCount === 'number' ? data.state.aiTurnCount :
    typeof data?.state?.turnCount === 'number' ? data.state.turnCount :
    typeof data?.fullData?.completedTurns === 'number' ? data.fullData.completedTurns :
    typeof data?.fullData?.aiTurnCount === 'number' ? data.fullData.aiTurnCount :
    typeof data?.fullData?.turnCount === 'number' ? data.fullData.turnCount :
    prev.completedTurns ?? 0;

  const maxTurns =
    typeof data?.maxTurns === 'number' ? data.maxTurns :
    typeof data?.state?.maxTurns === 'number' ? data.state.maxTurns :
    typeof data?.fullData?.maxTurns === 'number' ? data.fullData.maxTurns :
    prev.maxTurns ?? 0;

  const currentPhaseId =
    data?.currentPhaseId ??
    data?.currentPhase ??
    data?.state?.currentPhase ??
    data?.fullData?.currentPhase ??
    prev.currentPhaseId ?? null;

  const phaseHistoryLength =
    (Array.isArray(data?.phaseHistory) ? data.phaseHistory.length :
    Array.isArray(data?.state?.phaseHistory) ? data.state.phaseHistory.length :
    Array.isArray(data?.fullData?.phaseHistory) ? data.fullData.phaseHistory.length :
    prev.phaseHistoryLength ?? 0);

  const progressPercentage =
    typeof data?.progressPercentage === 'number' ? data.progressPercentage :
    (maxTurns > 0 ? Math.round((completedTurns / maxTurns) * 100) : 0);

  // Log de auditoría
  if (window.VITE_EPL_VERBOSE) {
    console.log('[METRICS_NORMALIZED]', {
      completedTurns, maxTurns, currentPhaseId, phaseHistoryLength, progressPercentage
    });
  }

  return {
    ...prev,
    completedTurns,
    maxTurns,
    currentPhaseId,
    phaseHistoryLength,
    progressPercentage,
    aiTurnCount: completedTurns // Para UI, displayTurns
  };
}

/**
 * 🛡️ Normalización de alertas del ModeradorIA
 * Desenrolla wrappers anidados y garantiza estructura plana
 * Soporta hasta 3 niveles de anidación: { moderatorAlert: { moderatorAlert: { ... } } }
 */
function normalizeModeratorAlert(raw) {
  if (!raw || typeof raw !== "object") return null;

  // Desenrollar wrappers repetidos moderatorAlert hasta 3 niveles
  let cur = raw;
  for (let i = 0; i < 3; i++) {
    if (cur && typeof cur === "object" && cur.moderatorAlert && typeof cur.moderatorAlert === "object") {
      // Si el objeto actual NO tiene active/message/action pero el interno sí,
      // asumimos que el interno es el payload real.
      const hasSignals = ("active" in cur) || ("message" in cur) || ("action" in cur);
      const innerHasSignals = ("active" in cur.moderatorAlert) || ("message" in cur.moderatorAlert) || ("action" in cur.moderatorAlert);

      if (!hasSignals && innerHasSignals) {
        cur = cur.moderatorAlert;
        continue;
      }

      // Si ambos tienen señales, preferimos el que tenga active boolean
      const curHasActiveBool = typeof cur.active === "boolean";
      const innerHasActiveBool = typeof cur.moderatorAlert.active === "boolean";

      if (!curHasActiveBool && innerHasActiveBool) {
        cur = cur.moderatorAlert;
        continue;
      }
    }
    break;
  }

  // Validación final: debe ser objeto
  if (!cur || typeof cur !== "object") return null;

  // active: preferir boolean explícito; si no existe, NO inventar
  const active = typeof cur.active === "boolean" ? cur.active : false;

  // Logging verbose
  if (window.VITE_EPL_VERBOSE) {
    console.log('[🛡️ MODERATOR_NORMALIZED]', {
      raw_input: raw,
      normalized_output: { ...cur, active },
      unwrap_iterations: i
    });
  }

  return { ...cur, active };
}

/**
 * VERTEX CHAT WINDOW
 * ==================
 * Interfaz de chat específica para agentes Vertex con panel de métricas integrado.
 * 
 * Diferencias con ChatWindow genérico:
 * - Panel de métricas en tiempo real (desktop: lateral derecha, móvil: colapsable)
 * - Procesamiento de metadata del backend (friction, interventions, intent)
 * - Botón "Ver Evaluación" al finalizar sesión
 * - Alertas visuales para intervenciones críticas
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  X, AlertTriangle,
  Volume2, VolumeX, Loader2, ChevronLeft,
  BarChart2, ChevronDown, ChevronUp
} from 'lucide-react';
import MetricsPanel from './MetricsPanel';
import MetricsDrawer from './MetricsDrawer';
import SessionStatusBadge from './SessionStatusBadge'; // 🎟️ TICKET P0
import useBreakpoint from '../../../hooks/useBreakpoint';
import { useAuth } from '../../../context/AuthContext'; // 🎟️ V4-P0-007: Admin-only metrics
import CoachHintBubble from './CoachHintBubble';
import ModeratorAlert from './ModeratorAlert';
import CoachHelpPill from './CoachHelpPill';
import BriefPedagogico from './BriefPedagogico';
import VoiceInputUX from '../../../components/VoiceInputUX'; // P0: UX Voz integrada
import { getSafeToken } from '../../../utils/tokenHelper';
import { VERTEX_API_BASE } from '../../../utils/vertexApiHelper';
import { textToSpeech } from '../../../services/voiceApiService'; // P0: Auto-play
import safeFetchJson from '../../../utils/safeFetchJson'; // 🎟️ V4-P0-007: Robust error handling
import ErrorDisplay from './ErrorDisplay'; // 🎟️ V4-P0-007: Error UI component

/**
 * Renderiza texto con formato markdown simple (inline)
 * Soporta: *cursiva*, **negrita**, "comillas destacadas", saltos de línea
 */
const renderMarkdownText = (text) => {
  if (!text) return null;

  // Dividir por líneas para preservar saltos
  const lines = text.split('\n');
  
  return lines.map((line, lineIndex) => {
    if (!line.trim()) {
      return <br key={`br-${lineIndex}`} />;
    }

    // Procesar inline markdown (incluye apóstrofes simples y comillas dobles)
    const parts = [];
    let currentIndex = 0;
    const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*|"([^"]+)"|'([^']+)')/g;
    let match;

    while ((match = regex.exec(line)) !== null) {
      // Texto antes del match
      if (match.index > currentIndex) {
        parts.push(line.substring(currentIndex, match.index));
      }

      // Match procesado
      if (match[2]) {
        // **negrita**
        parts.push(<strong key={`b-${lineIndex}-${match.index}`} className="font-semibold">{match[2]}</strong>);
      } else if (match[3]) {
        // *cursiva*
        parts.push(<em key={`i-${lineIndex}-${match.index}`} className="italic">{match[3]}</em>);
      } else if (match[4]) {
        // "comillas dobles" - énfasis para diálogo (negrita + color)
        parts.push(<span key={`q-${lineIndex}-${match.index}`} className="font-semibold text-indigo-700">"{match[4]}"</span>);
      } else if (match[5]) {
        // 'apóstrofes simples' - énfasis para diálogo (negrita + color)
        parts.push(<span key={`q-${lineIndex}-${match.index}`} className="font-semibold text-indigo-700">'{match[5]}'</span>);
      }

      currentIndex = match.index + match[0].length;
    }

    // Texto restante
    if (currentIndex < line.length) {
      parts.push(line.substring(currentIndex));
    }

    return (
      <span key={`line-${lineIndex}`}>
        {parts.length > 0 ? parts : line}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    );
  });
};

/**
 * CONTRATO DEFINITIVO BRIEF v1 - Renderizado determinista
 * Cero heurísticas, cero regex, cero "a veces sí, a veces no"
 */
const renderBriefMessage = (message) => {
  // CONTRATO v1: Fuentes de verdad obligatorias (NO fallbacks)
  const escenarioTexto = message?.payload?.escenario?.texto || "";

  const actorNombre = message?.payload?.actor?.nombre || "";
  const actorRol = message?.payload?.actor?.rol || "";
  const actorOrg = message?.payload?.actor?.organizacion || "";

  // CONTRATO v1: NO inferencia - Backend debe enviar pregunta.texto separada

  return (
    <div className="briefMessage bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-sm">
      {/* A) Encabezado de Brief (label) */}
      <div className="briefLabel text-xs font-semibold uppercase tracking-wide text-gray-600 opacity-70 mb-3 flex items-center gap-2">
        <span>Contexto del caso</span>
      </div>

      {/* B) Actor (si existe) */}
      {actorNombre && (
        <div className="briefActorBlock mb-3 pb-2 border-b border-blue-100">
          <div className="briefActorName font-semibold text-indigo-700 text-sm">
            {actorNombre}
          </div>
          {(actorRol || actorOrg) && (
            <div className="briefActorMeta text-sm text-gray-600 opacity-80">
              {actorRol && actorOrg ? `${actorRol} • ${actorOrg}` : actorRol || actorOrg}
            </div>
          )}
        </div>
      )}

      {/* C) Escenario - Procesar Markdown para negritas pero sin color en comillas */}
      <div className="briefContext text-gray-700 text-sm leading-relaxed mb-3">
        {renderMarkdownText(escenarioTexto)}
      </div>
    </div>
  );
};

/**
 * Helper: Detectar sintaxis Markdown válida
 */
const hasMarkdown = (text = "") =>
  /(\*\*|__|`|~~|\[.*?\]\(.*?\))/.test(text);

/**
 * CONTRATO DEFINITIVO BRIEF v1 - Detección determinista
 * ÚNICO CRITERIO VÁLIDO: brief_v === 1
 */
const isBriefMessage = (message) => {
  const result = message?.payload?.brief_v === 1;
  
  // DEBUG: Log para ver por qué no detecta BRIEF
  if (message?.payload || message?.content?.includes('CONTEXTO')) {
    console.log('🔍🔍 [isBriefMessage DEBUG]', {
      hasPayload: !!message?.payload,
      payloadKeys: message?.payload ? Object.keys(message.payload) : null,
      brief_v: message?.payload?.brief_v,
      brief_v_type: typeof message?.payload?.brief_v,
      result,
      contentPreview: message?.content?.substring(0, 60)
    });
  }
  
  return result;
};

const VertexChatWindow = ({ agent, session, apiEndpoint, onExit, onCancelOnboarding, confirmOnboarding, onViewEvaluation, onGenerateEvaluation, hideRightPanel = false }) => {
  console.log('[BRIEF PROBE] ===== VERTEXCHATWINDOW COMPONENT RENDER =====');
  console.log('[BRIEF PROBE] Props received - session:', session);
  console.log('[BRIEF PROBE] Props received - session.briefing:', session?.briefing);
  console.log('[BRIEF PROBE] Props received - session.phase:', session?.phase);
  
  // 🎟️ V4-P0-007: Admin-only MetricsPanel
  const { user } = useAuth();
  const isAdmin = user?.email === 'davidprudencio@hotmail.com';
  
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isCoachProcessing, setIsCoachProcessing] = useState(false);
  
  // FASE DE SESIÓN (onboarding, simulation_active, finished)
  const [sessionPhase, setSessionPhase] = useState(session?.phase || 'simulation_active');
  const [isConfirmingOnboarding, setIsConfirmingOnboarding] = useState(false);
  
  // Métricas de sesión - REGLA 1: Inicializar con DEFAULT_METRICS (nunca null)
  const [sessionMetrics, setSessionMetrics] = useState(DEFAULT_METRICS);
  const [currentPhase, setCurrentPhase] = useState('WARMUP');
  const [phaseHistory, setPhaseHistory] = useState([]);
  const [isSessionComplete, setIsSessionComplete] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('active'); // 'active', 'paused', 'armed_to_close', 'completed'
  const [scenarioInfo, setScenarioInfo] = useState(null); // { title, description }
  
  // 🎟️ TICKET P0: Estados para visualización Motor V4
  const [sessionStatusAfter, setSessionStatusAfter] = useState('active'); // Estado DESPUÉS del turno
  const [currentPhaseDisplayName, setCurrentPhaseDisplayName] = useState('');
  const [turnsInPhase, setTurnsInPhase] = useState(0);
  const [startedTurns, setStartedTurns] = useState(0);
  
  // 🎵 AUDIO QUEUE: Control de reproducción secuencial
  const briefAudioCompletedRef = useRef(false);
  const kickoffPendingRef = useRef(null);
  
  // 🎵 AUDIO QUEUE: Reproducción secuencial (BRIEF → KICKOFF)
  const audioQueueRef = useRef([]);
  const isPlayingFromQueueRef = useRef(false);
  const [closeUiMessage, setCloseUiMessage] = useState('');

  // Fuente de verdad: backend (session/state y/o response de /turn)
  const [aiTurnCount, setAiTurnCount] = useState(() => {
    const fromSession = session?.aiTurnCount ?? session?.state?.aiTurnCount;
    return typeof fromSession === 'number' ? fromSession : 0;
  });
  
  // UI
  // Responsive: fuente de verdad única
  const { isDesktop, isCompact } = useBreakpoint();
  const [showMetricsDrawer, setShowMetricsDrawer] = useState(false);
  const [isTTSEnabled, setIsTTSEnabled] = useState(true); // Control de voz (TTS) - Auto-habilitado
  
  // ========== P0: AUTO-PLAY AUDIO SYSTEM ==========
  // Estado de audio por mensaje: { messageId: { status: 'idle'|'generating'|'playing', audioBase64: string } }
  const [audioStates, setAudioStates] = useState({});
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);
  
  // 🎯 P0/P1 VOICE UX PREMIUM: Estado de reproducción de audio IA (TTS)
  // idle: Sin audio reproduciéndose / playing: Audio IA activo
  const [assistantAudioState, setAssistantAudioState] = useState('idle');
  
  // 🧠 P0 ASSISTANTSTATE: Estado de procesamiento IA (independiente de audio)
  // idle: IA no está procesando | thinking: IA generando respuesta | responding: IA emitiendo respuesta
  const [assistantState, setAssistantState] = useState('idle');
  
  const audioCache = useRef({}); // Cache de audios generados { messageId: { audioBase64, contentType } }
  
  // FASE 2: Audio Engine Único Persistente
  const audioEngineRef = useRef(null); // Elemento <audio> persistente (creado una vez)
  
  // audioPlayerRef deprecated - ahora usamos audioEngineRef
  const audioPlayerRef = useRef(null); // DEPRECATED: mantener temporalmente para compatibilidad
  
  const audioUnlockedRef = useRef(false); // 📱 Flag REAL verificado por eventos
  const [showAudioUnlockPrompt, setShowAudioUnlockPrompt] = useState(false); // 🔓 UI para desbloquear audio
  
  // FASE 1: Control de modal por sesión (máximo 1 vez)
  const hasShownModalRef = useRef(false);
  const unlockInProgressRef = useRef(false);
  
  // FASE 1: Cola serial de reproducción
  const audioQueue = useRef([]);
  const isProcessingQueueRef = useRef(false);
  
  // AUD-P1-22: Refs para mensaje pendiente (evitar stale closure en onClick)
  const pendingMessageIdRef = useRef(null);
  const pendingCacheKeyRef = useRef(null);
  
  // 🔍 VERSION CHECK - Para debug de caché
  useEffect(() => {
    console.log('🔵🔵🔵 [VERSION] VertexChatWindow v7.4.0-ANDROID-SKIP-WEBAUDIO 🔇');
    console.log('✅ [ANDROID FIX] WebAudio deshabilitado en data URL (S22 fix)');
    console.log('✅ [iOS < 17] Pre-carga audio (iPhone 13, 14)');
    console.log('✅ [EXIT FIX] Audio se detiene al salir');
    console.log('🔍 BUSCA: ANDROID-SKIP-WEBAUDIO');
    console.log('📱 [VERSION] audioUnlockedRef disponible:', typeof audioUnlockedRef);
    
    // P0 CHECK: Verificar existencia de unlockAudio en boot
    // NOTA: unlockAudio se define más abajo en el componente, este log ejecuta ANTES
    console.log('[AUDIO][BOOT] unlockAudio estará disponible después del render completo');
    console.log('[AUDIO][BOOT] Component mounted - audio system ready');
    
    // FASE 1 (AJUSTADO): NO resetear audioUnlocked - será verificado por eventos
    // sessionStorage se usa solo para tracking de modal, no para unlock status
    const modalShown = sessionStorage.getItem('audioModalShown');
    if (modalShown) {
      hasShownModalRef.current = true;
      console.log('📊 [SESSION] Modal ya se mostró en esta sesión:', modalShown);
    }
    
    // FASE 2: Crear Audio Engine único persistente
    if (!audioEngineRef.current) {
      console.log('🎵 [AUDIO ENGINE] Creando elemento persistente...');
      const audioElement = new Audio();
      audioElement.playsInline = true;
      audioElement.preload = 'auto';
      audioEngineRef.current = audioElement;
      
      // Mantener compatibilidad temporal con audioPlayerRef
      audioPlayerRef.current = audioElement;
      
      console.log('✅ [AUDIO ENGINE] Elemento persistente creado');
    }
    
    // Cleanup: NO destruir el engine (es persistente)
    return () => {
      console.log('🧹 [AUDIO ENGINE] Component unmounting (engine persiste)');
    };
  }, []);
  
  // Resetear flags de audio cuando cambia la sesión
  useEffect(() => {
    if (session?.sessionId) {
      console.log('🔄 [SESSION CHANGE] Reseteando flags de audio para nueva sesión:', session.sessionId);
      hasShownModalRef.current = false;
      audioUnlockedRef.current = false;
      hasAutoPlayedInitialRef.current = false;
      hasAutoPlayedBriefingRef.current = false;
      audioQueue.current = [];
      isProcessingQueueRef.current = false;
      sessionStorage.removeItem('audioModalShown');
      console.log('✅ [SESSION CHANGE] Flags reseteados');
    }
  }, [session?.sessionId]);
  // ========== FIN P0 ==========
  
  // Coach Hint Bubbles (V3 - inline, no modal)
  const [expandedHints, setExpandedHints] = useState({});
  
  // Modal de confirmación para salir
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // CTA de evaluación (fin natural): loading local para feedback inmediato
  const [isGeneratingEvaluationCta, setIsGeneratingEvaluationCta] = useState(false);
  
  // Moderador y sistema de ayuda
  const [moderatorAlert, setModeratorAlert] = useState({ active: false });
  const [helpRequestsCount, setHelpRequestsCount] = useState(0);
  const [lastHelpRequestTurn, setLastHelpRequestTurn] = useState(0);
  const [maxHelpRequests, setMaxHelpRequests] = useState(5);
  const [helpCooldown, setHelpCooldown] = useState(1);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasSentExitSignalRef = useRef(false);
  const allowBestEffortCancelRef = useRef(false);
  const hadRealInteractionRef = useRef(false);
  const sessionStatusRef = useRef('active');
  const hasAiTurnRef = useRef(false);
  const mountedAtRef = useRef(Date.now());

  // � INSTRUMENTACIÓN P0: Refs para tracking de freeze (15 ene 2026)
  const lastResponseRef = useRef(null);
  const freezeStartTimeRef = useRef(null);
  const freezeDetectedRef = useRef(false);

  // �🛡️ [AUDIT] Monitorear cambios en moderatorAlert STATE
  useEffect(() => {
    console.log('[MOD_ALERT_STATE]', {
      active: moderatorAlert?.active,
      action: moderatorAlert?.action,
      message: moderatorAlert?.message,
      full_object: moderatorAlert,
      timestamp: Date.now()
    });
  }, [moderatorAlert]);

  // 🔬 INSTRUMENTACIÓN P0: Watchdog para detectar freeze (15 ene 2026)
  useEffect(() => {
    const isInputBlocked = sessionStatus !== 'active';
    const isModalVisible = moderatorAlert?.active === true;
    const isFreezeCondition = isInputBlocked && !isModalVisible && !isSessionComplete;

    if (isFreezeCondition && !freezeStartTimeRef.current) {
      freezeStartTimeRef.current = Date.now();
      console.warn('[FREEZE_START]', {
        sessionId: session?.sessionId,
        sessionStatus,
        moderatorAlert,
        timestamp: freezeStartTimeRef.current
      });
    }

    if (!isFreezeCondition && freezeStartTimeRef.current) {
      console.log('[FREEZE_RESOLVED]', {
        sessionId: session?.sessionId,
        durationMs: Date.now() - freezeStartTimeRef.current
      });
      freezeStartTimeRef.current = null;
      freezeDetectedRef.current = false;
    }

    if (isFreezeCondition && freezeStartTimeRef.current && !freezeDetectedRef.current) {
      const freezeDuration = Date.now() - freezeStartTimeRef.current;
      
      if (freezeDuration > 3000) {
        freezeDetectedRef.current = true;
        
        console.error('[FRONTEND_FREEZE_DETECTED]', {
          timestamp: Date.now(),
          sessionId: session?.sessionId,
          sessionStatus,
          moderatorAlert,
          lastCorrelationId: lastResponseRef.current?.correlationId,
          lastResponsePayload: lastResponseRef.current,
          freezeDurationMs: freezeDuration
        });

        try {
          fetch('/api/telemetry/freeze', {
            method: 'POST',
            keepalive: true,
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              type: 'PAUSE_SESSION_FREEZE',
              sessionId: session?.sessionId,
              correlationId: lastResponseRef.current?.correlationId,
              sessionStatus,
              moderatorAlert,
              timestamp: Date.now(),
              freezeDurationMs: freezeDuration,
              userAgent: navigator.userAgent
            })
          }).catch(() => null);
        } catch (err) {
          console.warn('[FREEZE_TELEMETRY] Error:', err);
        }

        console.log('[FREEZE_AUTO_CORRECTION] Activando modal de emergencia');
        setModeratorAlert({
          active: true,
          action: 'pause_session',
          message: 'La sesión ha sido pausada. Si el problema persiste, contacta soporte técnico.',
          severity: 'high'
        });
      }
    }
  }, [sessionStatus, moderatorAlert, isSessionComplete, session?.sessionId]);

  useEffect(() => {
    sessionStatusRef.current = sessionStatus;
  }, [sessionStatus]);

  useEffect(() => {
    hasAiTurnRef.current = Array.isArray(messages) && messages.some(m => m?.role === 'agent');
  }, [messages]);

  // Habilitar auto-cancel SOLO cuando la sesión ya está en modo conversación.
  // Durante onboarding, cualquier cancel automático puede matar la sesión antes del primer turno.
  useEffect(() => {
    const phase = session?.phase;
    if (phase === 'simulation_active') {
      allowBestEffortCancelRef.current = true;
    }
  }, [session?.phase]);

  // ========== INICIO: FIX METRICAS PANEL V4 (GUARD sessionId - 10 ene 2026) ==========
  // 🛡️ GUARD con sessionId: Evita memoria fantasma + retrocesos ilegales
  useEffect(() => {
    if (!session) return;

    setSessionMetrics(prev => {
      // 🛡️ GUARD 1: Nueva sesión real → reset controlado
      if (prev?.sessionId && prev.sessionId !== session.sessionId) {
        console.log('EPL_V4_METRICS|SESSION_RESET', {
          prevSessionId: prev.sessionId,
          newSessionId: session.sessionId
        });
        const next = normalizeMetrics(session, null);
        return {
          ...next,
          sessionId: session.sessionId,
          totalPhases: agent?.stateMachine?.phases?.length ?? 0,
          phasesCompleted: session.stateMachine?.phaseHistory?.length ?? 0,
        };
      }

      // 🛡️ GUARD 2: Misma sesión → NO permitir retrocesos
      const next = normalizeMetrics(session, prev);

      if (
        typeof prev?.completedTurns === 'number' &&
        typeof next?.completedTurns === 'number' &&
        next.completedTurns < prev.completedTurns
      ) {
        console.warn('EPL_V4_METRICS|GUARD_BLOCKED_REGRESSION', {
          prevTurns: prev.completedTurns,
          nextTurns: next.completedTurns,
          sessionId: session.sessionId,
        });
        return prev; // ⛔ Bloquear overwrite inválido
      }

      return {
        ...prev,
        ...next,
        sessionId: session.sessionId,
        totalPhases: agent?.stateMachine?.phases?.length ?? 0,
        phasesCompleted: session.stateMachine?.phaseHistory?.length ?? 0,
      };
    });

    setCurrentPhase(session.stateMachine?.currentPhase);
    setPhaseHistory(session.stateMachine?.phaseHistory || []);
  }, [session, agent]);
  // ========== FIN: FIX METRICAS PANEL V4 ==========

  const getVoiceRoleForMessage = (message) => {
    // 1. Narrador / Sistema
    if (message?.role === 'system' || message?.role === 'narrator') {
      return 'narrator';
    }

    // 2. KICKOFF (inicio de conversación) → AGENTE (voz principal)
    if (message?.metadata?.type === 'SYSTEM_KICKOFF') {
      console.log('🎬 [Voice Roles] KICKOFF detectado → client');
      return 'client';
    }
    
    // 3. Mensajes del coach → VOZ PEDAGOGICA
    if (message?.role === 'coach') {
      console.log('🎓 [Voice Roles] COACH detectado → coach');
      return 'coach';
    }
    
    // 4. Default: mensajes del agente IA → AGENTE
    return 'client';
  };
  
  /**
   * Genera y reproduce audio automáticamente para un mensaje
   * @param {string} text - Texto a convertir en audio
   * @param {string} messageId - ID del mensaje para tracking
   * @param {Object} message - Mensaje completo con metadata (para Voice Roles)
   * @param {boolean} prioritize - Si es true, se pone al inicio de la cola (BRIEF)
   */
  const generateAndPlayAudio = async (text, messageId, message = null, prioritize = false) => {
    if (!isTTSEnabled || !text || !messageId) return;

    // 🔒 ANTI-DUPLICACIÓN: Usar hash del contenido como clave de cache
    const contentHash = `hash-${text.substring(0, 50).replace(/\s+/g, '-')}-${text.length}`;
    
    // Si ya existe audio para este contenido (por hash), no regenerar
    if (audioCache.current[contentHash]) {
      console.log('💾 [AUTO-PLAY] ⏭️ Audio YA EXISTE en cache (detectado por contenido)');
      playAudioFromCache(contentHash, messageId);
      return;
    }

    // AUD-P0-16: Marcar como generando con timestamp para watchdog
    const generateStartTime = Date.now();
    setAudioStates(prev => ({
      ...prev,
      [messageId]: { status: 'generating', audioBase64: null, generateStartTime }
    }));
    
    console.log('🔄 [AUTO-PLAY] Llamando a textToSpeech API...');

    try {
      const startTime = Date.now();
      
      // 🎭 VOICE ROLES SYSTEM: Detectar agentId y voiceRole
      const agentId = agent?.agentId || 'default';
      const voiceRole = message ? getVoiceRoleForMessage(message) : 'client';
      
      console.log(`🎯 [Voice Roles] agentId: ${agentId}, voiceRole: ${voiceRole}`);
      
      // 🔧 MULTI-TENANT + VOICE ROLES: Enviar parámetros de voz por roles
      const response = await textToSpeech({
        text,
        agentId,
        voiceRole
        // voice, model, format, speed son gestionados por el backend según Voice Roles
      });
      
      const { audioBase64, contentType, provider } = response;
      
      // 🔍 LOG PROVIDER EN CHAT
      const roleEmoji = voiceRole === 'narrator' ? '📖' : 
                       voiceRole === 'coach' ? '🎓' : '🎭';
      const providerLabel = provider === 'google' ? '🟢 REPRODUCE GEMINI' : '🔵 REPRODUCE OPENAI';
      console.log(`%c${roleEmoji} ${providerLabel} (${voiceRole})`, 'font-size: 20px; font-weight: bold; color: ' + (provider === 'google' ? 'green' : 'blue'));
      console.log('🎵 [VERTEX CHAT] Provider activo:', provider, 'Role:', voiceRole);
      
      const duration = Date.now() - startTime;
      console.log(`✅ [AUTO-PLAY] 🎙️ Audio generado en ${duration}ms`);

      // Guardar en cache POR CONTENIDO (no por messageId)
      audioCache.current[contentHash] = { audioBase64, contentType };
      console.log('💾 [AUTO-PLAY] Audio guardado en cache con hash:', contentHash);

      // DEBUG iOS: Verificar estado antes de reproducir
      console.log('📱 [DEBUG iOS] Estado pre-play:', {
        audioUnlocked: audioUnlockedRef.current,
        cacheExists: !!audioCache.current[contentHash],
        messageId,
        contentHash
      });

      // Reproducir automáticamente
      await playAudioFromCache(contentHash, messageId);

    } catch (error) {
      console.error('❌ [AUTO-PLAY] Error generando audio:', error);
      // AUD-P0-16: Siempre cerrar estado en error
      setAudioStates(prev => ({
        ...prev,
        [messageId]: { status: 'error', audioBase64: null }
      }));
    } finally {
      // AUD-P0-16: GARANTÍA - Si después de 5s aún está en generating, forzar cierre
      setTimeout(() => {
        setAudioStates(prev => {
          const currentState = prev[messageId];
          if (currentState?.status === 'generating') {
            console.warn('⚠️ [WATCHDOG] Estado colgado en generating, forzando needsUnlock');
            return {
              ...prev,
              [messageId]: { 
                ...currentState,
                status: 'blocked',  // Asumir que Safari bloqueó
                needsUnlock: !audioUnlockedRef.current
              }
            };
          }
          return prev;
        });
      }, 5000);
    }
    
    console.log('🎵 [AUTO-PLAY] === FIN generateAndPlayAudio ===');
  };

  /**
   * Reproduce audio desde el cache
   * @param {string} cacheKey - Clave del cache (hash del contenido)
   * @param {string} displayId - ID para mostrar en UI
   */
  const playAudioFromCache = async (cacheKey, displayId) => {
    console.log('▶️ [PLAYBACK] === INICIO playAudioFromCache ===');
    console.log('▶️ [PLAYBACK] cacheKey:', cacheKey);
    console.log('▶️ [PLAYBACK] displayId:', displayId);
    console.log('📱 [DEBUG iOS] audioUnlockedRef.current:', audioUnlockedRef.current);
    console.log('📱 [DEBUG iOS] Cache disponible:', !!audioCache.current[cacheKey]);
    
    const cached = audioCache.current[cacheKey];
    if (!cached) {
      console.log('❌ [PLAYBACK] No hay audio en cache para esta clave');
      return;
    }
    
    // 🚨 FASE 1 CRÍTICO: Verificar unlock ANTES de intentar play()
    if (!audioUnlockedRef.current) {
      console.log('🔒 [PLAYBACK] Audio no desbloqueado - encolando y mostrando modal');
      
      // Agregar a cola de reproducción
      const alreadyQueued = audioQueue.current.some(item => item.displayId === displayId);
      if (!alreadyQueued) {
        audioQueue.current.push({ cacheKey, displayId });
        console.log(`📋 [QUEUE] Audio ${displayId} encolado (${audioQueue.current.length} en cola)`);
      }
      
      // Marcar como bloqueado
      setAudioStates(prev => ({
        ...prev,
        [displayId]: {
          audioBase64: cached.audioBase64,
          cacheKey,
          needsUnlock: true,
          status: 'blocked'
        }
      }));
      
      // Mostrar modal si es primera vez en sesión
      if (!hasShownModalRef.current) {
        console.log('🎭 [MODAL] Primera vez - mostrando modal de activación');
        setShowAudioUnlockPrompt(true);
        hasShownModalRef.current = true;
        sessionStorage.setItem('audioModalShown', Date.now().toString());
      } else {
        console.log('ℹ️ [MODAL] Ya se mostró - audio esperará en cola hasta unlock manual');
      }
      
      return; // NO continuar con play() - esperar unlock
    }
    
    console.log('✅ [PLAYBACK] Audio desbloqueado - reproduciendo directamente');

    
    // TICKET 5 (P1): Validar firma del audio (diagnóstico)
    const firstBytes = cached.audioBase64.substring(0, 8);
    console.log('🔍 [VALIDATION] Audio signature:', firstBytes, '(debe ser SUQz para MP3 con ID3)');

    // Detener audio actual si existe
    if (audioPlayerRef.current) {
      console.log('⏹️ [PLAYBACK] Deteniendo audio anterior');
      const prevAudio = audioPlayerRef.current;
      prevAudio.pause();
      prevAudio.currentTime = 0;
      // AUD-P0-09: Limpieza completa para evitar contaminación (Safari)
      prevAudio.muted = true;  // Silenciar el anterior
      prevAudio.src = '';      // Liberar referencia
      prevAudio.load();        // Safari limpia mejor así
    }

    console.log('🔊 [PLAYBACK] Creando elemento <audio> con post-procesamiento...');

    // 🔧 NORMALIZAR CONTENT-TYPE para compatibilidad cross-browser
    let normalizedContentType = cached.contentType || 'audio/mpeg';
    
    // Safari/Opera requieren 'audio/mpeg' exacto (no 'audio/mp3')
    if (normalizedContentType === 'audio/mp3') {
      normalizedContentType = 'audio/mpeg';
    }
    
    console.log('🔍 [PLAYBACK] Content-Type original:', cached.contentType);
    console.log('🔍 [PLAYBACK] Content-Type normalizado:', normalizedContentType);
    console.log('🔍 [PLAYBACK] Base64 length:', cached.audioBase64?.length);

    // 🎯 DETECCIÓN SAFARI iOS: Usar data URL directamente (Blob URLs fallan con WebKitBlobResource error 1)
    // iOS 17+ FIX: UserAgent más genérico, usar fallbacks
    const hasIOSUA = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const hasIOSPlatform = /iPhone|iPad|iPod|Macintosh/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isiOSDevice = hasIOSUA || hasIOSPlatform;
    const isSafariIOS = isiOSDevice && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|OPiOS/.test(navigator.userAgent);
    
    // 🔧 FIX S22: Chrome Android 10+ oculta modelo por privacidad (muestra "Android 10; K")
    // No podemos detectar Samsung específicamente, pero sabemos que Exynos tiene problemas
    // Solución: Usar data URL para Android genérico + Edge Android (más seguro)
    
    // 🔍 DIAGNÓSTICO: Imprimir UserAgent completo para ajustar regex
    console.log('🔍 [S22-FIX] UserAgent completo:', navigator.userAgent);
    
    const isAndroidGeneric = /Android/.test(navigator.userAgent) && 
                             /; K\)/.test(navigator.userAgent); // "Android X; K)" = modelo oculto
    const isEdgeAndroid = /EdgA/.test(navigator.userAgent); // Microsoft Edge en Android
    const isSamsungGalaxy = /SM-[ASGN]\d{3}/.test(navigator.userAgent); // Fallback para versiones antiguas
    
    // Si es Android sin modelo identificable O Edge Android O Samsung detectado → data URL
    const useDataURL = isSafariIOS || isAndroidGeneric || isEdgeAndroid || isSamsungGalaxy;
    
    console.log('📱 [S22-FIX] Safari iOS detectado:', isSafariIOS);
    console.log('📱 [S22-FIX] Android genérico (modelo oculto):', isAndroidGeneric);
    console.log('📱 [S22-FIX] Edge Android:', isEdgeAndroid);
    console.log('📱 [S22-FIX] Samsung Galaxy (legacy):', isSamsungGalaxy);
    console.log('📱 [S22-FIX] Usando data URL:', useDataURL);
    
    let audioUrl;
    let isBlob = false;
    
    if (useDataURL) {
      // Safari iOS + Samsung Galaxy: usar data URL directamente (no Blob)
      audioUrl = `data:${normalizedContentType};base64,${cached.audioBase64}`;
      console.log('✅ [S22-FIX] Usando data URL (iOS/Samsung fix)');
    } else {
      // Otros navegadores: usar Blob URL (mejor rendimiento)
      try {
        const byteCharacters = atob(cached.audioBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: normalizedContentType });
        audioUrl = URL.createObjectURL(blob);
        isBlob = true;
        console.log('✅ [PLAYBACK] Usando Blob URL (desktop/Android)');
      } catch (blobError) {
        // Fallback a data URL
        audioUrl = `data:${normalizedContentType};base64,${cached.audioBase64}`;
        console.warn('⚠️ [PLAYBACK] Blob fallback falló, usando data URL:', blobError.message);
      }
    }

    // FASE 2: Usar Audio Engine persistente (NO crear new Audio())
    // CRÍTICO: Solo cambiar src, nunca recrear elemento
    
    // 🔧 iOS 13 FIX: Usar audio pre-cargado si existe (preserva gesto)
    let audio;
    if (window.__iOS13PreloadedAudio) {
      console.log('✅ [iOS 13 FIX] Usando audio pre-cargado del gesto');
      audio = window.__iOS13PreloadedAudio;
      window.__iOS13PreloadedAudio = null; // Limpiar
      audioEngineRef.current = audio; // Actualizar ref
      audioPlayerRef.current = audio;
    } else {
      audio = audioEngineRef.current;
      if (!audio) {
        console.error('❌ [AUDIO ENGINE] Engine no inicializado!');
        return;
      }
      console.log('🔄 [AUDIO ENGINE] Reutilizando engine persistente - solo cambiando src');
    }
    
    // Detener reproducción actual si existe
    if (!audio.paused) {
      console.log('⏹️ [AUDIO ENGINE] Pausando reproducción anterior');
      audio.pause();
    }
    
    // Cambiar solo el src (no recrear elemento) - SKIP si ya pre-cargado
    if (!window.__iOS13PreloadedAudio) {
      audio.src = audioUrl;
      audio.playsInline = true;  // TICKET 3: Evita fullscreen en iOS
      audio.preload = 'auto';
      
      // TICKET 3 (P0): Safari necesita load() explícito antes de play()
      audio.load();
    }
    
    // Mantener compatibilidad con audioPlayerRef
    audioPlayerRef.current = audio;

    // 🔒 TICKET AUD-P0-09: Normalizar estado del <audio> antes de reproducción
    // Safari puede heredar estados (especialmente muted) entre reproducciones
    
    // 🔴 P0-SAMSUNG-MUTED-FIX: FORZAR unmute antes de play()
    // El unlock silencioso deja muted: true en el engine persistente
    audio.muted = false;
    audio.volume = 1.0;
    
    console.log('🔊 [SAMSUNG FIX] Audio unmuted forzosamente antes de play()');
    
    // 🧪 Logs de validación completos (QA Safari)
    console.log('🔍 [AUDIO STATE] Estado normalizado pre-play:', {
      muted: audio.muted,
      defaultMuted: audio.defaultMuted,
      volume: audio.volume,
      currentTime: audio.currentTime,
      paused: audio.paused,
      readyState: audio.readyState,
      networkState: audio.networkState
    });

    // 🎛️ POST-PROCESAMIENTO: Voz autoritativa de inversionista (opcional, con fallback)
    let postProcessingApplied = false;
    let audioContext = null;
    
    // P0-B: Safari iOS + Android data URL - PROHIBIDO WebAudio (error "already associated")
    // Reproducir directo sin post-procesamiento
    const skipWebAudio = isSafariIOS || useDataURL;
    
    if (skipWebAudio) {
      console.log('📱 [SKIP WEBAUDIO] Post-procesamiento deshabilitado (iOS o Android data URL)');
      postProcessingApplied = false;
      audioContext = null;
    } else {
      try {
        // Verificar compatibilidad del navegador
        if (!window.AudioContext && !window.webkitAudioContext) {
          throw new Error('AudioContext not supported');
        }
      
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // AUD-P0-09: Safari/iOS puede suspender el AudioContext
      if (audioContext.state === 'suspended') {
        console.log('🔄 [AUDIOCONTEXT] Estado suspended, intentando resume con timeout...');
        
        // P0 SAFARI: audioContext.resume() puede colgarse - agregar timeout
        const resumePromise = audioContext.resume();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('AudioContext.resume() timeout')), 2000)
        );
        
        try {
          await Promise.race([resumePromise, timeoutPromise]);
          console.log('✅ [AUDIOCONTEXT] Resume exitoso');
        } catch (err) {
          console.warn('⚠️ [AUDIOCONTEXT] Resume falló o timeout:', err.message);
          // Continuar de todas formas - el play() puede funcionar
        }
      }
      console.log('🔍 [AUDIOCONTEXT] Estado final:', audioContext.state);
      
      const source = audioContext.createMediaElementSource(audio);
      
      // 1. High-pass filter (eliminar ruido bajo)
      const highPass = audioContext.createBiquadFilter();
      highPass.type = 'highpass';
      highPass.frequency.value = 80;
      highPass.Q.value = 0.7;
      
      // 2. Bass boost (voz más profunda/autoritativa)
      const bassBoost = audioContext.createBiquadFilter();
      bassBoost.type = 'lowshelf';
      bassBoost.frequency.value = 200;
      bassBoost.gain.value = 6;  // +6dB en bajos
      
      // 3. Compresión dinámica (voz más potente/presente)
      const compressor = audioContext.createDynamicsCompressor();
      compressor.threshold.value = -24;
      compressor.knee.value = 30;
      compressor.ratio.value = 8;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.25;
      
      // Cadena de procesamiento
      source.connect(highPass)
            .connect(bassBoost)
            .connect(compressor)
            .connect(audioContext.destination);
      
        postProcessingApplied = true;
        console.log('🎛️ [PLAYBACK] Post-procesamiento aplicado: High-pass + Bass +6dB + Compresión 8:1');
      } catch (err) {
        console.warn('⚠️ [PLAYBACK] No se pudo aplicar post-procesamiento (reproducción directa):', err.message);
        // El audio se reproducirá de forma directa sin procesamiento
      }
    }

    // Marcar como reproduciendo (usar displayId para UI)
    const messageId = displayId || cacheKey;
    
    // 🔧 FIX: Limpiar TODOS los estados "playing" anteriores de forma atómica
    setAudioStates(prev => {
      const newState = {};
      
      // Copiar todos los estados, pero cambiar "playing" a "idle"
      for (const key in prev) {
        if (prev[key].status === 'playing') {
          newState[key] = { ...prev[key], status: 'idle' };
        } else {
          newState[key] = prev[key];
        }
      }
      
      // AUD-P0-20: NO marcar como 'playing' aquí - esperar confirmación de play()
      // Solo preparamos el estado sin status
      newState[displayId] = { 
        status: 'loading',  // Estado transitorio hasta play() resuelva
        audioBase64: cached.audioBase64, 
        cacheKey 
      };
      
      return newState;
    });
    // FASE 3: Event listeners con validación REAL por eventos
    // Limpiar listeners anteriores (engine persistente puede tener listeners previos)
    audio.onended = null;
    audio.onerror = null;
    audio.onplaying = null; // FASE 3: Nuevo listener
    
    // FASE 3: Evento 'playing' - confirmación REAL de reproducción
    audio.onplaying = () => {
      console.log('✅ [FASE 3] Evento "playing" disparado - audio REALMENTE reproduciéndose');
      
      // AHORA SÍ marcar como playing (confirmado por evento DOM)
      setAudioStates(prev => ({
        ...prev,
        [displayId]: { 
          status: 'playing', 
          audioBase64: cached.audioBase64, 
          cacheKey, 
          needsUnlock: false 
        }
      }));
      setCurrentlyPlayingId(displayId);
      
      // 🎯 P0/P1: Marcar audio IA como reproduciéndose
      setAssistantAudioState('playing');
      
      // Marcar unlock como verificado
      audioUnlockedRef.current = true;
      
      console.log('🔍 [FASE 3] PLAY_OK validado por evento "playing" (NO por Promise)');
    };
    
    audio.onended = () => {
      console.log('✅ [PLAYBACK] Audio terminado, marcando como idle');
      
      // Limpiar Blob URL para evitar memory leak
      if (isBlob && audioUrl) {
        URL.revokeObjectURL(audioUrl);
        console.log('🧹 [PLAYBACK] Blob URL liberado');
      }
      
      // TICKET 4 (P1): State machine - ready para replay
      setAudioStates(prev => ({
        ...prev,
        [displayId]: { status: 'ready', audioBase64: cached.audioBase64, cacheKey, needsUnlock: false }
      }));
      setCurrentlyPlayingId(null);
      
      // 🎯 P0/P1: Liberar estado de audio IA
      setAssistantAudioState('idle');
      
      // 🎯 P0/P1: Liberar estado de audio IA
      setAssistantAudioState('idle');
      
      // 🎬 Si era el BRIEF y hay un KICKOFF pendiente, reproducirlo ahora
      if (displayId === 'brief-session-briefing') {
        console.log('📖 [BRIEF SEQUENCE] BRIEF completado, marcando flag');
        briefAudioCompletedRef.current = true;
        
        if (kickoffPendingRef.current) {
          console.log('🎬 [BRIEF SEQUENCE] Reproduciendo KICKOFF pendiente...');
          const { text, messageId: kickoffId, message } = kickoffPendingRef.current;
          kickoffPendingRef.current = null;
          
          // Delay pequeño para transición suave
          setTimeout(() => {
            generateAndPlayAudio(text, kickoffId, message);
          }, 300);
        }
      }
    };

    audio.onerror = (e) => {
      console.error('❌ [PLAYBACK] Error reproduciendo audio');
      
      // TICKET 5 (P1): Observabilidad Safari iOS
      console.error('❌ [PLAYBACK] Error details:', {
        error: e,
        audioError: audio.error,
        errorCode: audio.error?.code,
        errorMessage: audio.error?.message,
        contentType: normalizedContentType,
        base64Length: cached.audioBase64?.length,
        srcLength: audio.src?.length,
        usedBlob: isBlob,
        // TICKET 5: Diagnóstico Safari iOS
        readyState: audio.readyState,
        networkState: audio.networkState,
        blobType: isBlob ? normalizedContentType : 'N/A',
        blobSize: isBlob ? cached.audioBase64?.length : 'N/A'
      });
      
      // TICKET 1 (P0): NO revocar aquí - Safari puede estar aún cargando
      // La revocación solo ocurre en onended
      
      // TICKET 4 (P1): State machine - error real
      setAudioStates(prev => ({
        ...prev,
        [displayId]: { status: 'error', audioBase64: cached.audioBase64, cacheKey, needsUnlock: false }
      }));
      setCurrentlyPlayingId(null);
      
      // 🎯 P0/P1: Liberar estado de audio IA en error
      setAssistantAudioState('idle');
    };

    // Reproducir con manejo de autoplay policy
    // AUD-P0-20: NO marcar como 'playing' hasta confirmar que play() resolvió
    console.log('[AUDIO][PRE_PLAY] 🎬 A punto de ejecutar audio.play()...');
    console.log('[AUDIO][PRE_PLAY] Audio element state:', {
      readyState: audio.readyState,
      paused: audio.paused,
      muted: audio.muted,
      src: audio.src ? 'SET' : 'NOT SET'
    });
    
    const playPromise = audio.play();
    console.log('[AUDIO][PRE_PLAY] audio.play() llamado, esperando Promise...');
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('✅ [PLAYBACK] play() Promise resuelto');
          console.log('[AUDIO][PLAY_OK] audio.play() resolvió exitosamente');
          
          // FASE 3: El evento 'onplaying' ya debería haber disparado
          // (se dispara síncronamente antes de que esta Promise resuelva)
          console.log('ℹ️ [FASE 3] Promise resuelta - evento "playing" ya debería haber disparado');
        })
        .catch(err => {
          console.error('❌ [PLAYBACK] Autoplay bloqueado por navegador:', err.name);
          console.error('❌ [PLAYBACK] Error completo:', err);
          
          // Si llegamos aquí es porque play() falló REALMENTE (no debería pasar si verificamos unlock antes)
          console.log('[AUDIO][PLAY_REJECTED]', err.name, err.message);
          console.warn('⚠️ [PLAYBACK] play() falló aunque audioUnlocked=true - posible error de red/codec');
          
          // Marcar como error
          setAudioStates(prev => ({
            ...prev,
            [displayId]: {
              audioBase64: cached.audioBase64,
              cacheKey,
              needsUnlock: false, // Ya estaba desbloqueado, es otro tipo de error
              status: 'error'
            }
          }));
          setCurrentlyPlayingId(null);
          
          // 🎯 P0/P1: Liberar estado de audio IA en error de Promise
          setAssistantAudioState('idle');
        });
    }
    
    console.log('▶️ [PLAYBACK] === FIN playAudioFromCache ===');
  };
  
  // 🎯 P0/P1 VOICE UX PREMIUM: Detener audio TTS explícitamente
  const stopAudioPlayback = () => {
    console.log('⏹️ [STOP AUDIO] Deteniendo reproducción de audio IA');
    
    const audio = audioEngineRef.current;
    if (!audio) {
      console.warn('⚠️ [STOP AUDIO] No hay audio engine disponible');
      return;
    }
    
    // Pausar y resetear audio
    if (!audio.paused) {
      audio.pause();
      audio.currentTime = 0;
      console.log('✅ [STOP AUDIO] Audio pausado y reseteado');
    }
    
    // Limpiar estado de reproducción
    if (currentlyPlayingId) {
      setAudioStates(prev => ({
        ...prev,
        [currentlyPlayingId]: {
          ...prev[currentlyPlayingId],
          status: 'ready'
        }
      }));
      setCurrentlyPlayingId(null);
    }
    
    // Liberar estado de audio IA
    setAssistantAudioState('idle');
    
    console.log('✅ [STOP AUDIO] Estado liberado - mic e input habilitados');
  };
  
  // 🔓 UNLOCK AUDIO (para móviles con autoplay policy)
  // FASE 1: Validación REAL por eventos (no Promise optimista)
  const unlockAudio = () => {
    return new Promise((resolve, reject) => {
      console.log('[AUDIO][UNLOCK_AUDIO] ✅ FUNCIÓN LLAMADA - unlockAudio() ejecutándose');
      console.log('🔴🔴🔴 [MOBILE] FUNCIÓN UNLOCK AUDIO - INICIO');
      console.log('📱 [DEBUG iOS] audioUnlocked antes de unlock:', audioUnlockedRef.current);
      
      // Guard de idempotencia - evitar múltiples unlocks
      if (audioUnlockedRef.current) {
        console.log('⚠️ [MOBILE] Audio ya desbloqueado, ignorando llamada');
        resolve(true);
        return;
      }
      
      if (unlockInProgressRef.current) {
        console.log('⚠️ [MOBILE] Unlock ya en progreso, ignorando llamada');
        resolve(false);
        return;
      }
      
      unlockInProgressRef.current = true;
      
      try {
        console.log('🔓 [S22-FIX] Desbloqueando audio context...');
        
        // 🔧 FIX: iOS necesita MP3, Android genérico necesita WAV
        // iOS 17+ FIX: UserAgent más genérico, usar múltiples métodos de detección
        const hasIOSUA = /iPhone|iPad|iPod/.test(navigator.userAgent);
        const hasIOSPlatform = /iPhone|iPad|iPod|Macintosh/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const isiOS = hasIOSUA || hasIOSPlatform;
        
        let silentAudio;
        if (isiOS) {
          // iOS: MP3 silencioso (funciona correctamente)
          const silentMP3 = 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMD/////////////////////////////////////////////////////////////////AAAAAExhdmM1OC4xMzQAAAAAAAAAAAAAAAAkAiQAAAAAAAABhEgq4G8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQZAAAAANIAAAAAExBTUUzLjk5LjVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';
          console.log('🎵 [S22-FIX] iOS detectado - usando MP3 silencioso...');
          silentAudio = new Audio(silentMP3);
        } else {
          // Android: WAV silencioso (más compatible con Exynos)
          const silentWAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
          console.log('🎵 [S22-FIX] Android detectado - usando WAV silencioso...');
          silentAudio = new Audio(silentWAV);
        }
        
        // Configuración Safari iOS determinista
        silentAudio.playsInline = true;
        silentAudio.muted = true;
        silentAudio.preload = 'auto';
        
        // 🔧 Flag para evitar doble manejo de error
        let errorHandled = false;
        
        // ✅ VALIDACIÓN REAL: Esperar evento 'playing' (no solo Promise)
        const playingHandler = () => {
          if (errorHandled) return;
          errorHandled = true;
          console.log('✅ [MOBILE] ¡Evento "playing" recibido! Audio REALMENTE desbloqueado');
          audioUnlockedRef.current = true;
          unlockInProgressRef.current = false;
          resolve(true);
        };
        
        const errorHandler = (err) => {
          if (errorHandled) {
            console.log('ℹ️ [MOBILE] Error event ignorado (ya manejado por catch)');
            return;
          }
          errorHandled = true;
          console.error('❌ [MOBILE] Error en audio silencioso:', err);
          unlockInProgressRef.current = false;
          reject(err);
        };
        
        // Timeout de seguridad (2s)
        const timeoutId = setTimeout(() => {
          if (errorHandled) return;
          errorHandled = true;
          silentAudio.removeEventListener('playing', playingHandler);
          silentAudio.removeEventListener('error', errorHandler);
          console.warn('⚠️ [MOBILE] Timeout - asumiendo unlock exitoso (Safari quirk)');
          audioUnlockedRef.current = true;
          unlockInProgressRef.current = false;
          resolve(true);
        }, 2000);
        
        silentAudio.addEventListener('playing', () => {
          clearTimeout(timeoutId);
          playingHandler();
        }, { once: true });
        
        silentAudio.addEventListener('error', (err) => {
          clearTimeout(timeoutId);
          errorHandler(err);
        }, { once: true });
        
        console.log('▶️ [MOBILE] Intentando reproducir audio silencioso...');
        silentAudio.play().catch(err => {
          if (errorHandled) return; // Ya manejado por timeout o evento
          errorHandled = true;
          
          clearTimeout(timeoutId);
          silentAudio.removeEventListener('playing', playingHandler);
          silentAudio.removeEventListener('error', errorHandler);
          
          console.error('❌ [S22-FIX] play() rechazado:', err.name, err.message);
          
          // NotSupportedError en iOS = formato rechazado pero unlock funciona igual
          // AbortError = ya está playing
          if (err.name === 'AbortError' || err.name === 'NotSupportedError') {
            console.warn(`⚠️ [MOBILE] ${err.name} - considerando como éxito (unlock logrado)`);
            audioUnlockedRef.current = true;
            unlockInProgressRef.current = false;
            resolve(true);
          } else {
            unlockInProgressRef.current = false;
            reject(err);
          }
        });
        
      } catch (error) {
        console.error('💥 [MOBILE] ERROR CRÍTICO en unlockAudio:', error);
        unlockInProgressRef.current = false;
        reject(error);
      }
    });
  };
  
  // 🎵 FASE 1: Cola serial de reproducción de audio
  // Procesa automáticamente la cola de mensajes pendientes
  const processAudioQueue = async () => {
    if (isProcessingQueueRef.current) {
      console.log('⚠️ [QUEUE] Ya hay un procesador activo, ignorando llamada');
      return;
    }
    
    if (audioQueue.current.length === 0) {
      console.log('ℹ️ [QUEUE] Cola vacía, nada que procesar');
      return;
    }
    
    console.log(`🎵 [QUEUE] Iniciando procesamiento de ${audioQueue.current.length} audios pendientes`);
    isProcessingQueueRef.current = true;
    
    while (audioQueue.current.length > 0) {
      const { cacheKey, displayId } = audioQueue.current[0];
      console.log(`▶️ [QUEUE] Procesando audio ${displayId} (${audioQueue.current.length} restantes)`);
      
      try {
        // Reproducir y esperar a que termine antes de procesar siguiente
        await playAudioFromCache(cacheKey, displayId);
        console.log(`✅ [QUEUE] Audio ${displayId} completado`);
      } catch (err) {
        console.error(`❌ [QUEUE] Error reproduciendo ${displayId}:`, err);
      }
      
      // Remover de la cola después de procesar (exitoso o no)
      audioQueue.current.shift();
    }
    
    console.log('✅ [QUEUE] Procesamiento de cola completado');
    isProcessingQueueRef.current = false;
  };
  
  // ========== FIN P0: AUTO-PLAY ==========

  const exitSession = async () => {
    if (isExiting) return;

    // 🔇 DETENER AUDIO antes de salir
    console.log('🔇 [EXIT] Deteniendo audio antes de salir...');
    if (audioEngineRef.current) {
      try {
        audioEngineRef.current.pause();
        audioEngineRef.current.currentTime = 0;
        audioEngineRef.current.src = '';
        console.log('✅ [EXIT] Audio detenido correctamente');
      } catch (err) {
        console.warn('⚠️ [EXIT] Error deteniendo audio:', err);
      }
    }
    if (audioPlayerRef.current && audioPlayerRef.current !== audioEngineRef.current) {
      try {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.currentTime = 0;
        audioPlayerRef.current.src = '';
      } catch (err) {
        console.warn('⚠️ [EXIT] Error deteniendo audioPlayerRef:', err);
      }
    }

    setIsExiting(true);
    setShowExitConfirmation(false);
    hasSentExitSignalRef.current = true;

    try {
      const sessionId = session?.sessionId;
      const isV4 = Boolean(session?.isV4);
      if (isV4 && sessionId) {
        const token = getSafeToken();
        if (token) {
          await fetch(`${VERTEX_API_BASE}/v4/session/cancel`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              sessionId,
              reason: 'USER_EXIT',
              endReasonDetail: 'modal_exit'
            })
          });
        }
      }
    } catch (_) {
      // salida explícita: best-effort, nunca bloquear UX
    } finally {
      try {
        await onExit?.();
      } finally {
        setIsExiting(false);
      }
    }
  };

  // ✅ Homologación cierre (Opción 1) — Ticket experto P0: best-effort exit/cancel en tab_close/back/route_change
  useEffect(() => {
    const sessionId = session?.sessionId;
    const isV4 = Boolean(session?.isV4);
    if (!isV4 || !sessionId) return;

    mountedAtRef.current = Date.now();

    const sendCancelBestEffort = (detail) => {
      // No cancelar automáticamente mientras el usuario aún está en onboarding.
      if (!allowBestEffortCancelRef.current) return;

      // Hardening: no enviar cancel si no existió interacción real.
      // Protege contra StrictMode (mount/unmount) y desmontajes tempranos.
      const hasUserInteraction = Boolean(hadRealInteractionRef.current);
      const hasAiTurn = Boolean(hasAiTurnRef.current);
      if (!hasUserInteraction && !hasAiTurn) return;
      if (hasSentExitSignalRef.current) return;
      // No enviar si ya está marcada como completed/closed localmente
      if (sessionStatusRef.current !== 'active') return;

      try {
        const token = getSafeToken();
        if (!token) return;

        hasSentExitSignalRef.current = true;

        // keepalive permite que el request sobreviva a unload (best-effort)
        fetch(`${VERTEX_API_BASE}/v4/session/cancel`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sessionId,
            reason: 'USER_EXIT',
            endReasonDetail: detail
          }),
          keepalive: true
        }).catch(() => null);
      } catch (_) {
        // best-effort: nunca bloquear UX
      }
    };

    const onPageHide = () => sendCancelBestEffort('tab_close');
    const onBeforeUnload = () => sendCancelBestEffort('tab_close');
    const onPopState = () => sendCancelBestEffort('browser_back');

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('popstate', onPopState);

    return () => {
      // Desmontaje por navegación interna.
      // Mitigación StrictMode (DEV): el primer mount/unmount falso ocurre casi inmediato.
      // Evitar que ese cleanup dispare cancel si no hubo interacción real.
      const elapsedMs = Date.now() - mountedAtRef.current;
      const shouldSkipLikelyStrictMode = elapsedMs < 1000 && !hadRealInteractionRef.current;
      if (!shouldSkipLikelyStrictMode) {
        sendCancelBestEffort('route_change');
      }
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPopState);
    };
    // Dependencias mínimas para evitar re-registrar listeners
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId, session?.isV4]);

  const getAvatarFallbackText = (name) => {
    const value = typeof name === 'string' ? name.trim() : '';
    if (!value) return 'AI';
    const parts = value.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const second = parts[1]?.[0] || '';
    const initials = `${first}${second}`.toUpperCase();
    return initials || value.slice(0, 2).toUpperCase();
  };

  const shouldUseComfortMinWidth = (msg) => {
    if (msg?.role !== 'user') return false;
    const text = typeof msg?.content === 'string' ? msg.content.trim() : '';
    if (!text) return false;
    // Si hay varias palabras, evitar burbuja ultra angosta
    return text.split(/\s+/).filter(Boolean).length >= 2;
  };

  // 🎟️ TCK-P0-V4-FE-RENDER-GUARDS-CLOSEPAYLOAD
  const normalizeVisibleText = (value) => {
    if (typeof value !== 'string') return '';

    // Quitar whitespace + caracteres invisibles comunes que generan burbujas “vacías”
    // (zero-width, BOM, etc.)
    return value.replace(/[\s\u200B\u200C\u200D\uFEFF]+/g, ' ').trim();
  };

  const isTimeOnlyText = (value) => {
    const normalized = normalizeVisibleText(value);
    if (!normalized) return false;
    // Ejemplos: 00:38, 0:38, 00:38:12
    return /^\d{1,2}:\d{2}(:\d{2})?$/.test(normalized);
  };

  const isUsefulText = (value) => {
    const normalized = normalizeVisibleText(value);
    if (!normalized) return false;
    // Guard extra: no renderizar “mensajes” que son solo hora
    if (isTimeOnlyText(normalized)) return false;
    return true;
  };

  const normalizeComparableText = (value) => normalizeVisibleText(value);
  const shouldRenderChatBubble = (msg) => {
    if (!msg) return false;

    // Brief tiene renderer dedicado
    if (isBriefMessage(msg)) return true;

    // CoachHintBubble se renderiza por coachAlert (no depende de content)
    if (msg.role === 'coach-user-column') return true;

    // Spinner de coach requiere texto
    if (msg.role === 'coach-analyzing') return isUsefulText(msg.content);

    // Mensajes normales
    return isUsefulText(msg.content);
  };

  // 🎟️ TCK-P1-UX-MODERATION-WARN-COPY-AND-CTA-V4 + P0-MODERATION-PAUSE-FIX
  // Estándar UX:
  // - warn: informativo (no bloquea)
  // - blocked (show_modal/pause_session): bloquea input hasta que usuario reformule
  // - end_session: bloquea input (cierre terminal)
  const isModerationBlockingInput = Boolean(
    moderatorAlert?.active && (moderatorAlert.action === 'show_modal' || moderatorAlert.action === 'pause_session' || moderatorAlert.action === 'end_session')
  );
  const isInputDisabled = isSessionComplete || sessionPhase === 'onboarding' || sessionStatus !== 'active' || isModerationBlockingInput;

  // 🔄 Auto-resize del textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'; // Reset para calcular reducción
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`; // Límite aumentado (controlado por CSS max-h)
    }
  }, [inputText]);

  useEffect(() => {
    console.log('[BRIEF PROBE] ===== USEEFFECT SESSION CHANGE =====');
    console.log('[BRIEF PROBE] useEffect - session:', session);
    console.log('[BRIEF PROBE] useEffect - session.briefing:', session?.briefing);
    console.log('[BRIEF PROBE] useEffect - session.phase:', session?.phase);
    
    // Detectar fase inicial
    if (session?.phase) {
      setSessionPhase(session.phase);
    }

    // Sin inferencia: solo refrescar aiTurnCount si backend lo provee en session/state
    const nextAiTurnCount = session?.aiTurnCount ?? session?.state?.aiTurnCount;
    if (typeof nextAiTurnCount === 'number') {
      setAiTurnCount(nextAiTurnCount);
    }

    // ✅ BRIEF PEDAGÓGICO V4: Ya no procesamos briefing como mensaje
    // El briefing se renderiza directamente desde session.briefing usando BriefPedagogico component
    console.log('[VertexChatWindow] 📦 Session briefing disponible:', !!session?.briefing);
    console.log('[BRIEF PROBE] Session briefing DETAILED:', session?.briefing);
    
    // Inicializar mensajes vacíos si es simulation_active (el brief se renderiza por separado)
    if (session?.phase === 'simulation_active' && messages.length === 0) {
      console.log('[VertexChatWindow] ✅ Simulación activa - mensajes inicializados (brief renderizado por BriefPedagogico)');
      // No agregar briefing a messages - se renderiza con el componente BriefPedagogico
    }

    // NO cargar initialMessage - el onboarding ahora usa campos separados
    // Los mensajes del chat se inician con el briefing después de confirmar

    // Speech Recognition ahora manejado por MicButton component
    return () => {}; // Cleanup si es necesario
  }, [session]);

  // 🎟️ TCK-P1-V4-BRIEF-KICKOFF-UX-POLISH-AND-CONTRACT
  // Kickoff SOLO desde backend (metadata.type === SYSTEM_KICKOFF, idealmente payload.source === pre_roll).
  // Al rehidratar/refresh, deduplicar kickoff (mostrar 1 sola vez).
  useEffect(() => {
    if (sessionPhase !== 'simulation_active') return;

    const initial = Array.isArray(session?.initialMessages) ? session.initialMessages : [];
    if (initial.length === 0) return;

    const normalizeInitialMessage = (raw) => {
      if (!raw) return null;
      const content = typeof raw.content === 'string' ? raw.content : (typeof raw.text === 'string' ? raw.text : '');
      const normalizedContent = String(content || '').trim();
      if (!normalizedContent) return null;

      const timestampRaw = raw.timestamp || raw.createdAt || null;
      const timestamp = timestampRaw ? new Date(timestampRaw) : new Date();

      const speakerRole = raw.speakerRole || raw?.payload?.speakerRole || 'CLIENT';
      const role = speakerRole === 'COACH' ? 'coach' : (raw.role || 'agent');

      return {
        ...raw,
        role,
        speakerRole,
        content: normalizedContent,
        timestamp,
        metadata: { ...(raw.metadata || {}) },
        payload: raw.payload || undefined
      };
    };

    const normalized = initial.map(normalizeInitialMessage).filter(Boolean);

    const kickoffKeyStrict = (m) =>
      m?.metadata?.type === 'SYSTEM_KICKOFF' && m?.payload?.source === 'pre_roll';
    const kickoffKeyLoose = (m) => m?.metadata?.type === 'SYSTEM_KICKOFF';

    const deduped = [];
    let hasKickoffStrict = false;
    let hasKickoffLoose = false;

    for (const m of normalized) {
      if (kickoffKeyStrict(m)) {
        if (hasKickoffStrict) continue;
        hasKickoffStrict = true;
        hasKickoffLoose = true;
        deduped.push(m);
        continue;
      }
      if (kickoffKeyLoose(m)) {
        if (hasKickoffLoose) continue;
        hasKickoffLoose = true;
        deduped.push(m);
        continue;
      }
      deduped.push(m);
    }

    setMessages((prev) => {
      // No pisar conversación activa; sólo hidratar si aún no hay mensajes.
      if (Array.isArray(prev) && prev.length > 0) return prev;
      return deduped;
    });
  }, [sessionPhase, session?.sessionId, session?.initialMessages]);

  // ========== P0: AUTO-PLAY PARA MENSAJES INICIALES (BRIEF/KICKOFF) ==========
  // Se ejecuta DESPUÉS de que los mensajes se hayan cargado en el estado
  const hasAutoPlayedInitialRef = useRef(false);
  
  useEffect(() => {
    console.log('🔍 [INITIAL AUTO-PLAY useEffect] DISPARADO - messages.length:', messages.length, 'hasAutoPlayed:', hasAutoPlayedInitialRef.current);
    
    // Solo ejecutar una vez y solo si hay mensajes
    if (hasAutoPlayedInitialRef.current || messages.length === 0) {
      console.log('❌ [INITIAL AUTO-PLAY] ABORTADO:', {
        hasAutoPlayedInitialRef: hasAutoPlayedInitialRef.current,
        messagesLength: messages.length
      });
      return;
    }
    
    console.log('🔍🔍🔍 [INITIAL AUTO-PLAY] ==== INICIO ====');
    console.log('🔍 [INITIAL AUTO-PLAY] Total mensajes en estado:', messages.length);
    
    // Pequeño delay para asegurar que el DOM se haya renderizado
    const timer = setTimeout(() => {
      messages.forEach((msg, idx) => {
        const isAgentMessage = (msg.role === 'agent' || msg.role === 'assistant' || msg.role === 'model') && 
                              msg.speakerRole !== 'COACH' &&
                              msg.content && msg.content.trim();
        
        const isBrief = isBriefMessage(msg);
        const isKickoff = msg?.metadata?.type === 'SYSTEM_KICKOFF';
        
        const shouldAutoPlay = isAgentMessage || isBrief || isKickoff;
        
        if (shouldAutoPlay && msg.content && msg.content.trim()) {
          console.log(`🎵 [AUTO-PLAY] Procesando mensaje #${idx}:`, { isBrief, isKickoff });
          
          // Asegurar ID único
          if (!msg.id && !msg._id) {
            msg.id = `msg-initial-${Date.now()}-${idx}`;
          }
          
          const messageId = msg.id || msg._id;
          
          // 🎬 Si es KICKOFF, verificar si debe esperar al BRIEF
          if (isKickoff && !briefAudioCompletedRef.current) {
            console.log('⏸️ [BRIEF SEQUENCE] KICKOFF detectado pero BRIEF no ha terminado - poniendo en espera');
            kickoffPendingRef.current = { text: msg.content, messageId, message: msg };
          } else {
            // Reproducir inmediatamente (no es KICKOFF o BRIEF ya terminó)
            generateAndPlayAudio(msg.content, messageId, msg);
          }
        }
      });
      
      hasAutoPlayedInitialRef.current = true;
    }, 200); // Delay mínimo para asegurar renderizado (reducido de 800ms)
    
    return () => clearTimeout(timer);
  }, [messages.length]); // Se dispara cuando cambia la cantidad de mensajes
  // ========== FIN P0: AUTO-PLAY INICIAL ==========

  // ========== P0-A: AUTO-PLAY PARA SESSION.BRIEFING ==========
  // P0-A: BRIEF debe insertarse en messages[] (no estado paralelo)
  const hasAutoPlayedBriefingRef = useRef(false);
  
  useEffect(() => {
    const briefing = session?.briefing;
    const isSimActive = session?.phase === 'simulation_active';
    
    if (hasAutoPlayedBriefingRef.current || !briefing) return;
    
    // P0-A FIX: Si phase=simulation_active, BriefPedagogico ya renderiza el BRIEF visualmente
    // Solo necesitamos generar audio, NO insertarlo en messages[] (evita duplicación)
    if (isSimActive) {
      console.log('ℹ️ [BRIEF] Phase=simulation_active - BriefPedagogico renderiza, solo generando audio');
      
      // Construir texto para TTS pero NO insertar en messages
      const escenarioTexto = briefing?.escenario?.texto || '';
      const actorNombre = briefing?.actor?.nombre || '';
      const actorRol = briefing?.actor?.rol || '';
      const actorOrg = briefing?.actor?.organizacion || '';
      
      let fullText = '';
      if (actorNombre) {
        fullText += `${actorNombre}. `;
        if (actorRol || actorOrg) {
          fullText += `${actorRol && actorOrg ? `${actorRol}, ${actorOrg}` : actorRol || actorOrg}. `;
        }
      }
      fullText += escenarioTexto;
      
      if (fullText.trim()) {
        const briefMessage = {
          id: 'brief-session-briefing',
          role: 'model',
          content: fullText,
          payload: { brief_v: 1 },
          metadata: { type: 'BRIEF' }
        };
        
        console.log('🎵 [BRIEF AUDIO ONLY] Generando audio sin insertar en messages');
        setTimeout(() => {
          generateAndPlayAudio(fullText, briefMessage.id, briefMessage);
        }, 200); // Delay mínimo para renderizado (reducido de 800ms)
      }
      
      hasAutoPlayedBriefingRef.current = true;
      return;
    }
    
    // P0-A: Verificar si BRIEF ya existe en messages
    const briefAlreadyExists = messages.some(m => m.id === 'brief-session-briefing' || m?.payload?.brief_v === 1);
    if (briefAlreadyExists) {
      console.log('✅ [BRIEF] Ya existe en messages[], saltando');
      hasAutoPlayedBriefingRef.current = true;
      return;
    }
    
    // Construir texto completo del brief para TTS
    const escenarioTexto = briefing?.escenario?.texto || '';
    const actorNombre = briefing?.actor?.nombre || '';
    const actorRol = briefing?.actor?.rol || '';
    const actorOrg = briefing?.actor?.organizacion || '';
    
    let fullText = '';
    
    // Agregar actor si existe
    if (actorNombre) {
      fullText += `${actorNombre}. `;
      if (actorRol || actorOrg) {
        fullText += `${actorRol && actorOrg ? `${actorRol}, ${actorOrg}` : actorRol || actorOrg}. `;
      }
    }
    
    // Agregar escenario
    fullText += escenarioTexto;
    
    if (!fullText.trim()) return;
    
    console.log('📖 [BRIEF P0-A] Insertando BRIEF como primer mensaje en messages[]');
    console.log('📖 [BRIEF P0-A] Messages actuales:', messages.length);
    
    // P0-A: Insertar BRIEF en messages[] para unificar con auto-play inicial
    const briefMessage = {
      id: 'brief-session-briefing',
      role: 'model',
      content: fullText,
      payload: { brief_v: 1 }, // Para que isBriefMessage() lo detecte
      metadata: { type: 'BRIEF' },
      timestamp: new Date().toISOString()
    };
    
    // CRÍTICO: Usar función para obtener estado actual (prevenir stale state)
    setMessages(prevMessages => {
      // Re-verificar que no exista (por si hubo race condition)
      const alreadyExists = prevMessages.some(m => m.id === 'brief-session-briefing' || m?.payload?.brief_v === 1);
      if (alreadyExists) {
        console.log('⚠️ [BRIEF P0-A] Ya existe durante update, no insertando');
        return prevMessages;
      }
      
      const updatedMessages = [briefMessage, ...prevMessages];
      console.log('✅ [BRIEF P0-A] BRIEF insertado en position 0, messages.length:', updatedMessages.length);
      return updatedMessages;
    });
    
    hasAutoPlayedBriefingRef.current = true;
  }, [session?.briefing, messages.length]); // Agregar messages.length como dependencia
  // ========== FIN P0: AUTO-PLAY BRIEFING ==========

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // DEBUG: Validación cumplimiento Contrato Brief v1
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;

    const m0 = messages[0];
    const isBrief = isBriefMessage(m0);
    
    console.log("[BRIEF_CONTRATO_v1][COMPLIANCE_CHECK]", {
      // Criterio único válido
      brief_v: m0?.payload?.brief_v,
      detected: isBrief,
      
      // Campos obligatorios del contrato
      hasEscenario: !!m0?.payload?.escenario?.texto,
      hasPregunta: !!m0?.payload?.pregunta?.texto,
      hasUIVariant: !!m0?.payload?.ui?.variant,
      
      // Validación estructura
      payloadKeys: m0?.payload ? Object.keys(m0.payload) : null,
      contractCompliant: isBrief && m0?.payload?.escenario?.texto && m0?.payload?.ui?.variant
    });
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // toggleVoiceInput eliminado - ahora se usa MicButton

  const sendMessage = async () => {
    if (!inputText.trim() || isSending) return;

    // Marca mínima: hubo un turno real del usuario (aunque la IA falle luego).
    hadRealInteractionRef.current = true;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputText.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsSending(true);
    
    // 🧠 P0 ASSISTANTSTATE: IA toma el turno - BLOQUEO TOTAL
    setAssistantState('thinking');
    console.log('🧠 [ASSISTANTSTATE] Usuario envió mensaje → thinking (input/mic/send bloqueados)');
    
    // Mantener foco en input
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    try {
      const token = getSafeToken();
      
      // 🎟️ V4-P0-007: Usar safeFetchJson con timeout y manejo robusto de errores
      const result = await safeFetchJson(apiEndpoint, {
        method: 'POST',
        body: {
          sessionId: session.sessionId,
          message: userMessage.content
        },
        token,
        timeout: 30000, // 30 segundos
        verbose: window.VITE_EPL_VERBOSE
      });
      
      // 🎟️ V4-P0-007: Manejo de errores HTTP/Network
      if (!result.ok) {
        console.error('❌ [V4-P0-007] Error en /session/turn:', {
          status: result.status,
          error: result.error,
          correlationId: result.correlationId,
          duration: result.duration
        });
        
        // Insertar mensaje de error en timeline
        const errorMessage = {
          id: `error-${result.timestamp}`,
          role: 'system',
          speakerRole: 'SYSTEM',
          content: result.error || 'Error al procesar tu mensaje',
          timestamp: new Date(result.timestamp),
          metadata: { 
            type: 'SYSTEM_ERROR',
            correlationId: result.correlationId,
            httpStatus: result.status
          },
          isError: true,
          correlationId: result.correlationId
        };
        
        setMessages(prev => [...prev, errorMessage]);
        
        // 🧠 P0 ASSISTANTSTATE: Error → idle (desbloquear controles)
        setAssistantState('idle');
        console.log('🧠 [ASSISTANTSTATE] Error HTTP → idle (controles desbloqueados)');
        return;
      }
      
      const data = result.data;

      // 🔬 INSTRUMENTACIÓN P0: Guardar último response para debugging (15 ene 2026)
      lastResponseRef.current = {
        ...data,
        receivedTimestamp: Date.now(),
        correlationId: result.correlationId  // 🎟️ V4-P0-007: Rastreo de correlación
      };

      // ========== INICIO: DEBUG SESSION UPDATE (4 ene 2026) ==========
      console.log('🔍 [SESSION_UPDATE_DEBUG] Response completa del backend:', {
        success: data.success,
        sessionStatus: data.sessionStatus,
        completedTurns: data.completedTurns,
        aiTurnCount: data.aiTurnCount,
        maxTurns: data.maxTurns,
        currentPhase: data.currentPhase,
        phaseHistory: data.phaseHistory,
        state: data.state,
        sessionState: data.sessionState,
        fullData: data,
        correlationId: result.correlationId  // 🔬 P0: Incluir correlationId
      });
      
      // 🎟️ TICKET P0: Capturar datos para visualización Motor V4
      console.log('🎟️ [TICKET P0] Datos Motor V4 del backend:', {
        sessionStatusAfter: data.sessionStatusAfter || data.sessionStatus,
        phase: data.phase || data.currentPhase,
        currentPhaseDisplayName: data.currentPhaseDisplayName,
        turnsInPhase: data.turnsInPhase,
        startedTurns: data.startedTurns,
        maxTurns: data.maxTurns,
        'stateMachine.turnsInPhase': data.stateMachine?.turnsInPhase,
        'state.stateMachine.turnsInPhase': data.state?.stateMachine?.turnsInPhase
      });
      
      // Actualizar estados P0 INMEDIATAMENTE (antes de cualquier early return)
      // 🚨 CRÍTICO P0: Resolver estado ANTES de setear, con prioridad de pausa
      let effectiveSessionStatus = data.sessionStatusAfter || data.sessionStatus || 'active';
      
      // 🚨 OVERRIDE CRÍTICO: Si hay moderación activa con pause_session, SIEMPRE es 'paused'
      if (data.moderatorAlert?.action === 'pause_session' || 
          data.sessionStatus === 'paused' || 
          data.sessionStatusAfter === 'paused') {
        effectiveSessionStatus = 'paused';
        console.warn('[🚨 PAUSE_DETECTION] Estado forzado a paused:', {
          moderatorAlert_action: data.moderatorAlert?.action,
          sessionStatus: data.sessionStatus,
          sessionStatusAfter: data.sessionStatusAfter,
          effectiveSessionStatus
        });
      }
      
      // Setear estado efectivo una sola vez
      setSessionStatusAfter(effectiveSessionStatus);
      if (data.phase || data.currentPhase) {
        setCurrentPhase(data.phase || data.currentPhase);
      }
      if (data.currentPhaseDisplayName) {
        setCurrentPhaseDisplayName(data.currentPhaseDisplayName);
      }
      if (typeof data.turnsInPhase === 'number') {
        setTurnsInPhase(data.turnsInPhase);
      } else if (typeof data.stateMachine?.turnsInPhase === 'number') {
        setTurnsInPhase(data.stateMachine.turnsInPhase);
      } else if (typeof data.state?.stateMachine?.turnsInPhase === 'number') {
        setTurnsInPhase(data.state.stateMachine.turnsInPhase);
      }
      if (typeof data.startedTurns === 'number') {
        setStartedTurns(data.startedTurns);
      }
      // ========== FIN: DEBUG SESSION UPDATE ==========

      // 🛡️ [MODERACION_IA] Log completo de la respuesta del backend
      console.log('🛡️ [MODERACION_IA] ===== INICIO ANÁLISIS RESPUESTA BACKEND =====');
      console.log('🛡️ [MODERACION_IA] correlationId:', result.correlationId);  // 🔬 P0
      console.log('🛡️ [MODERACION_IA] data.success:', data.success);
      console.log('🛡️ [MODERACION_IA] data.moderatorAlert:', JSON.stringify(data.moderatorAlert, null, 2));
      console.log('🛡️ [MODERACION_IA] data.sessionStatus:', data.sessionStatus);
      console.log('🛡️ [MODERACION_IA] data.requiresResume:', data.requiresResume);
      console.log('🛡️ [MODERACION_IA] ===== FIN ANÁLISIS =====');

      // 🛡️ [MODERACION_IA] SET CANÓNICO DE MODERATOR ALERT (PRIORIDAD ALTA)
      // P0-FIX: Setear moderador ANTES de cualquier early return (ej: METRICS_FREEZE)
      // 🚨 HOTFIX P0 (15 ene 2026): Forzar modal si sessionStatus='paused' aunque moderatorAlert.active=false
      if ("moderatorAlert" in data) {
        const normalized = normalizeModeratorAlert(data.moderatorAlert);

        // HOTFIX: Si sesión pausada pero moderatorAlert inactivo/ausente, forzar activo
        if (data.sessionStatus === 'paused' && normalized?.active !== true) {
          const fallbackAlert = {
            active: true,
            type: 'warning',
            action: 'pause_session',
            message: normalized?.message || data.message || 'La sesión está pausada. Debes reanudarla para continuar.',
            severity: normalized?.severity || 'medium',
            suggestHelp: false
          };
          
          console.error('[PAUSE_MODAL_FALLBACK_APPLIED]', {
            event: 'PAUSE_MODAL_FALLBACK_APPLIED',
            sessionId: session?.sessionId,
            correlationId: data.correlationId,
            turn: data.completedTurns || data.aiTurnCount,
            sessionStatus: data.sessionStatus,
            moderatorAlert_in: normalized,
            moderatorAlert_out: fallbackAlert,
            timestamp: Date.now()
          });
          
          setModeratorAlert(fallbackAlert);
          console.log('[MOD_ALERT_SET_CALLED] CANONICAL-FALLBACK - setModeratorAlert(fallback)');
          
          // 🚨 SYNC CRÍTICO: Forzar sessionStatusAfter='paused' para que badge verde se actualice
          setSessionStatusAfter('paused');
          console.warn('[🚨 PAUSE_STATUS_SYNC] Forzando sessionStatusAfter=paused por moderatorAlert');
        } else if (normalized?.active === true) {
          setModeratorAlert(normalized);
          console.log('[MOD_ALERT_SET_CALLED] CANONICAL - setModeratorAlert(normalized)', {
            action: normalized.action,
            severity: normalized.severity,
            message: normalized.message?.substring(0, 80)
          });
          
          // 🚨 SYNC CRÍTICO: Si acción es pause_session, forzar sessionStatusAfter='paused'
          if (normalized.action === 'pause_session') {
            setSessionStatusAfter('paused');
            console.warn('[🚨 PAUSE_STATUS_SYNC] Forzando sessionStatusAfter=paused por moderatorAlert.action=pause_session');
          }
        } else if (normalized?.active === false) {
          setModeratorAlert({ active: false });
          console.log('[MOD_ALERT_SET_CALLED] CANONICAL - setModeratorAlert({active:false})');
        }
      } else if (data.sessionStatus === 'paused') {
        // HOTFIX: moderatorAlert ausente pero sesión pausada
        const fallbackAlert = {
          active: true,
          type: 'warning',
          action: 'pause_session',
          message: data.message || 'La sesión está pausada. Debes reanudarla para continuar.',
          severity: 'medium',
          suggestHelp: false
        };
        
        console.error('[PAUSE_MODAL_FALLBACK_APPLIED]', {
          event: 'PAUSE_MODAL_FALLBACK_APPLIED',
          sessionId: session?.sessionId,
          correlationId: data.correlationId,
          turn: data.completedTurns || data.aiTurnCount,
          sessionStatus: data.sessionStatus,
          moderatorAlert_in: null,
          moderatorAlert_out: fallbackAlert,
          timestamp: Date.now()
        });
        
        setModeratorAlert(fallbackAlert);
        console.log('[MOD_ALERT_SET_CALLED] CANONICAL-FALLBACK-ABSENT - setModeratorAlert(fallback)');
        
        // 🚨 SYNC CRÍTICO: Forzar sessionStatusAfter='paused'
        setSessionStatusAfter('paused');
        console.warn('[🚨 PAUSE_STATUS_SYNC] Forzando sessionStatusAfter=paused por sessionStatus=paused (sin moderatorAlert)');
      }

      if (!data.success) {
        // Manejar sesión pausada
        if (data.requiresResume || data.sessionStatus === 'paused') {
          setSessionStatus('paused');
          
          // 🚨 HOTFIX P0 (15 ene 2026): SIEMPRE forzar modal activo cuando sesión pausada
          let finalAlert = null;
          
          if ("moderatorAlert" in data) {
            const normalized = normalizeModeratorAlert(data.moderatorAlert);
            if (normalized?.active) {
              console.log('🛡️ [MODERACION_IA][PUNTO_1] Seteando alerta normalizada:', normalized);
              finalAlert = normalized;
            } else {
              // HOTFIX: Backend envió active:false pero sesión pausada → forzar activo
              console.warn('🛡️ [MODERACION_IA][PUNTO_1] Backend envió active:false con sesión pausada - aplicando fallback');
              finalAlert = {
                active: true,
                type: 'warning',
                action: 'pause_session',
                message: normalized?.message || data.message || 'La sesión está pausada. Debes reanudarla para continuar.',
                severity: normalized?.severity || 'medium',
                suggestHelp: false
              };
              
              console.error('[PAUSE_MODAL_FALLBACK_APPLIED]', {
                event: 'PAUSE_MODAL_FALLBACK_APPLIED',
                point: 'PUNTO_1',
                sessionId: session?.sessionId,
                correlationId: data.correlationId,
                moderatorAlert_in: normalized,
                moderatorAlert_out: finalAlert,
                timestamp: Date.now()
              });
            }
          } else {
            // Backend no envió moderatorAlert, crear fallback local
            console.warn('🛡️ [MODERACION_IA][PUNTO_1] moderatorAlert ausente con sesión pausada - aplicando fallback');
            finalAlert = {
              active: true,
              type: 'warning',
              action: 'pause_session',
              message: data.message || 'La sesión está pausada. Debes reanudarla para continuar.',
              severity: 'medium',
              suggestHelp: false
            };
            
            console.error('[PAUSE_MODAL_FALLBACK_APPLIED]', {
              event: 'PAUSE_MODAL_FALLBACK_APPLIED',
              point: 'PUNTO_1',
              sessionId: session?.sessionId,
              correlationId: data.correlationId,
              moderatorAlert_in: null,
              moderatorAlert_out: finalAlert,
              timestamp: Date.now()
            });
          }
          
          setModeratorAlert(finalAlert);
          console.log('[MOD_ALERT_SET_CALLED] PUNTO_1 - setModeratorAlert(finalAlert)');
          return;
        }
        
        // Manejar sesión completada (puede ser por moderador)
        if (data.sessionStatus === 'completed') {
          // 🛡️ P0-MODERATOR-NORMALIZATION: Normalizar antes de setear
          if ("moderatorAlert" in data) {
            const normalized = normalizeModeratorAlert(data.moderatorAlert);
            if (normalized?.active) {
              console.log('🛡️ [MODERACION_IA][PUNTO_2] Seteando alerta normalizada:', normalized);
              setModeratorAlert(normalized);
              console.log('[MOD_ALERT_SET_CALLED] PUNTO_2 - setModeratorAlert(normalized)');
            } else {
              // NOTA: completed normalmente NO requiere modal forzado (sesión terminó naturalmente)
              // Solo limpiar si viene explícitamente active:false
              console.log('🛡️ [MODERACION_IA][PUNTO_2] Backend envió active:false, limpiando');
              setModeratorAlert({ active: false });
              console.log('[MOD_ALERT_SET_CALLED] PUNTO_2 - setModeratorAlert({active:false})');
            }
          }
          // Si data no trae moderatorAlert, NO tocar el estado (preservar alerta activa)
          setSessionStatus('completed');
          return;
        }
        
        throw new Error(data.message || 'Error al enviar mensaje');
      }
      
      // NUEVO: Procesar múltiples mensajes con roles separados
      const newMessages = [];
      let filteredEmptyMessagesCount = 0;
      let filteredTimeOnlyMessagesCount = 0;

      const closingUi = data?.ui || data?.closePayload?.ui || data?.payload?.ui || null;
      const shouldShowClosingMessage = Boolean(data?.sessionClosed === true && closingUi?.showClosingMessage === true);
      const closingMessageText = normalizeComparableText(closingUi?.message);
      
      // SOPORTE V3: Respuesta simple { text, sender, coachAlert }
      if (data.text) {
        // 🎟️ P0-MODERATION-FILTER: VALIDAR ANTES de procesar data.text
        const hasModerationAlert = data.moderatorAlert?.active === true;
        
        if (hasModerationAlert) {
          console.log('[P0-MODERATION-FILTER] data.text bloqueado - moderación activa, no renderizar en chat');
          filteredEmptyMessagesCount += 1;
        } else {
          const trimmed = normalizeComparableText(data.text);
          if (isTimeOnlyText(trimmed)) {
            filteredTimeOnlyMessagesCount += 1;
          } else if (isUsefulText(trimmed)) {
            newMessages.push({
              id: Date.now() + 1,
              role: 'agent',
              speakerRole: 'CLIENT',
              content: trimmed,
              timestamp: new Date(),
              metadata: { type: 'AI_RESPONSE' }
            });
          } else {
            filteredEmptyMessagesCount += 1;
          }
        }

        // DEBUG: Verificar si llega coachAlert
        console.log('[VertexChatWindow] coachAlert recibido:', data.coachAlert);

        // Si hay alerta de coach, actualizar el ÚLTIMO mensaje del usuario
        if (data.coachAlert && data.coachAlert.active) {
           console.log('[VertexChatWindow] ✅ Actualizando mensaje con coachAlert');
           setMessages(prev => {
             const userMessages = prev.filter(m => m.role === 'user');
             const lastUserMsg = userMessages[userMessages.length - 1];
             
             console.log('[VertexChatWindow] Último mensaje usuario:', lastUserMsg);
             
             return prev.map(m => 
               m.id === lastUserMsg?.id 
                 ? { ...m, coachAlert: data.coachAlert } 
                 : m
             );
           });
        } else {
           console.log('[VertexChatWindow] ❌ No hay coachAlert activo');
        }
      } else if (data.messages && Array.isArray(data.messages)) {
        // Backend envía array de mensajes con speakerRole
        for (const msg of data.messages) {
          const text = normalizeComparableText(msg?.text);

          // 🎟️ P0-MODERATION-FILTER: Excluir mensajes de moderación (se muestran solo en ModeratorAlert)
          const isModerationMessage = 
            msg.metadata?.type && ['MODERATION_WARN', 'MODERATION_PAUSE', 'MODERATION_END', 'MODERATION_BLOCK', 'SYSTEM', 'MODERATION'].includes(msg.metadata.type) ||
            msg.speakerRole === 'SYSTEM' ||
            msg.payload?.moderatorAlert ||
            msg.payload?.action && ['warn', 'pause_session', 'end_session', 'show_modal'].includes(msg.payload.action);
          
          if (isModerationMessage) {
            console.log('[P0-MODERATION-FILTER] Mensaje de moderación excluido del chat:', {
              metadataType: msg.metadata?.type,
              speakerRole: msg.speakerRole,
              payloadAction: msg.payload?.action,
              textPreview: text?.substring(0, 50)
            });
            filteredEmptyMessagesCount += 1;
            continue;
          }

          // Guard rail: no agregar mensajes sin texto útil
          if (isTimeOnlyText(text)) {
            filteredTimeOnlyMessagesCount += 1;
            continue;
          }
          if (!isUsefulText(text)) {
            filteredEmptyMessagesCount += 1;
            continue;
          }

          // No duplicar cierre si el backend lo incluyó también en messages[]
          if (shouldShowClosingMessage && isUsefulText(closingMessageText) && text === closingMessageText) {
            filteredEmptyMessagesCount += 1;
            continue;
          }

          newMessages.push({
            id: Date.now() + Math.random(),
            role: msg.speakerRole === 'COACH' ? 'coach' : 'agent',
            speakerRole: msg.speakerRole,
            content: text,
            timestamp: msg?.timestamp ? new Date(msg.timestamp) : new Date(),
            metadata: msg?.metadata?.type
              ? { ...(msg.metadata || {}) }
              : (msg.speakerRole === 'COACH' ? { type: 'AI_COACH' } : { type: 'AI_RESPONSE' }),
            payload: msg?.payload
          });
        }
      } else {
        // 🎟️ P0-MODERATION-FILTER: NO crear fallback si moderación está activa
        const hasModerationActive = data.moderatorAlert?.active === true;
        
        if (hasModerationActive) {
          console.log('[P0-MODERATION-FILTER] Fallback bloqueado - moderación activa, no inyectar texto del modal al chat');
          filteredEmptyMessagesCount += 1;
        } else {
          // Fallback: formato antiguo (solo response)
          const trimmed = normalizeComparableText(data.response);
          if (isTimeOnlyText(trimmed)) {
            filteredTimeOnlyMessagesCount += 1;
          } else if (isUsefulText(trimmed)) {
            newMessages.push({
              id: Date.now() + 1,
              role: 'agent',
              speakerRole: 'CLIENT',
              content: trimmed,
              timestamp: new Date(),
              metadata: { type: 'AI_RESPONSE' }
            });
          } else {
            filteredEmptyMessagesCount += 1;
          }
        }
      }

      if (filteredEmptyMessagesCount > 0 || filteredTimeOnlyMessagesCount > 0) {
        console.log(
          `[TCK-P0-V4-FE-RENDER-GUARDS-CLOSEPAYLOAD] Filtrados ${filteredEmptyMessagesCount} mensajes vacíos/duplicados + ${filteredTimeOnlyMessagesCount} mensajes solo-hora`
        );
      }

      setMessages(prev => {
        const merged = [...prev, ...newMessages];

        // Anti-duplicación kickoff (estricto: SYSTEM_KICKOFF + payload.source=pre_roll; fallback: SYSTEM_KICKOFF)
        const kickoffKeyStrict = (m) =>
          m?.metadata?.type === 'SYSTEM_KICKOFF' && m?.payload?.source === 'pre_roll';
        const kickoffKeyLoose = (m) => m?.metadata?.type === 'SYSTEM_KICKOFF';

        const cleaned = [];
        let hasKickoffStrict = false;
        let hasKickoffLoose = false;
        for (const m of merged) {
          if (kickoffKeyStrict(m)) {
            if (hasKickoffStrict) continue;
            hasKickoffStrict = true;
            hasKickoffLoose = true;
            cleaned.push(m);
            continue;
          }
          if (kickoffKeyLoose(m)) {
            if (hasKickoffLoose) continue;
            hasKickoffLoose = true;
            cleaned.push(m);
            continue;
          }
          cleaned.push(m);
        }

        return cleaned;
      });

      // 🧠 P0 ASSISTANTSTATE: IA terminó de generar respuesta → idle (desbloquear controles)
      setAssistantState('idle');
      console.log('🧠 [ASSISTANTSTATE] IA respondió → idle (controles desbloqueados)');

      // ========== P0: AUTO-PLAY AUDIO PARA MENSAJES DEL AGENTE ==========
      // Disparar generación de audio para cada mensaje nuevo del agente (paralelo, no bloqueante)
      console.log('🔍🔍🔍 [AUTO-PLAY DEBUG] ==== INICIO PROCESAMIENTO ====');
      console.log('🔍 [AUTO-PLAY DEBUG] Total mensajes nuevos:', newMessages.length);
      
      newMessages.forEach((msg, idx) => {
        console.log(`\n🔍 [AUTO-PLAY DEBUG] ===== Mensaje #${idx} =====`);
        console.log('📦 [AUTO-PLAY DEBUG] Estructura completa:', {
          role: msg.role,
          speakerRole: msg.speakerRole,
          hasContent: !!msg.content,
          contentLength: msg.content?.length,
          contentPreview: msg.content?.substring(0, 80),
          hasPayload: !!msg.payload,
          briefVersion: msg.payload?.brief_v,
          hasMetadata: !!msg.metadata,
          metadataType: msg?.metadata?.type,
          hasId: !!(msg.id || msg._id),
          id: msg.id || msg._id
        });
        
        // Detectar mensajes reproducibles:
        const isAgentMessage = (msg.role === 'agent' || msg.role === 'assistant' || msg.role === 'model') && 
                              msg.speakerRole !== 'COACH' &&
                              msg.content && msg.content.trim();
        
        const isBrief = isBriefMessage(msg);
        const isKickoff = msg?.metadata?.type === 'SYSTEM_KICKOFF';
        
        console.log('🎯 [AUTO-PLAY DEBUG] Evaluación:', {
          isAgentMessage,
          isBrief,
          isKickoff,
          shouldAutoPlay: isAgentMessage || isBrief || isKickoff
        });
        
        const shouldAutoPlay = isAgentMessage || isBrief || isKickoff;
        
        if (shouldAutoPlay && msg.content && msg.content.trim()) {
          console.log(`\n🚀🚀🚀 [AUTO-PLAY DEBUG] ¡SÍ! DISPARANDO AUDIO para mensaje #${idx}`);
          
          // CRÍTICO: Asegurar que el mensaje tenga un ID único
          if (!msg.id && !msg._id) {
            msg.id = `msg-${Date.now()}-${idx}`;
            console.log('🆔 [AUTO-PLAY] Generado ID nuevo:', msg.id);
          }
          
          const messageId = msg.id || msg._id;
          console.log('🎵 [AUTO-PLAY] Llamando generateAndPlayAudio con:', {
            messageId,
            textLength: msg.content.length,
            role: msg.role,
            isBrief,
            isKickoff
          });
          
          generateAndPlayAudio(msg.content, messageId, msg);
        } else {
          console.log(`\n❌ [AUTO-PLAY DEBUG] NO califica mensaje #${idx}:`, {
            shouldAutoPlay,
            hasContent: !!msg.content,
            contentTrimmed: !!msg.content?.trim()
          });
        }
      });
      console.log('🔍🔍🔍 [AUTO-PLAY DEBUG] ==== FIN PROCESAMIENTO ====\n');
      // ========== FIN P0: AUTO-PLAY ==========

      // 🎟️ TCK-P1-V4-CLOSE-UI-DEDUPE-AND-PRESERVE-LAST-AI
      // ui.message pertenece a UI chrome (banner/notice), NO al timeline.
      if (shouldShowClosingMessage && isUsefulText(closingMessageText)) {
        setCloseUiMessage(closingMessageText);
      }

      // 🐛 DEBUG: Verificar tokenUsage
      console.log('📊 [DEBUG] tokenUsage recibido del backend:', data.tokenUsage);

      // ========== ACTUALIZACIÓN DE MÉTRICAS (CRÍTICO - CON ERROR HANDLING) ==========
      try {
        // 📊 [METRICS DEBUG] Log estado PREVIO
        console.log('📊 [METRICS DEBUG] ===== ANTES DE ACTUALIZAR =====');
        console.log('📊 [METRICS DEBUG] moderatorAlert activo:', data.moderatorAlert?.active);
        console.log('📊 [METRICS DEBUG] sessionMetrics actuales:', {
          completedTurns: sessionMetrics?.completedTurns,
          aiTurnCount: sessionMetrics?.aiTurnCount,
          maxTurns: sessionMetrics?.maxTurns,
          turnCount: sessionMetrics?.turnCount
        });
        
        // 🎟️ P0-METRICS-DETERMINISTIC: Backend como única fuente de verdad
        // Resolución explícita con typeof para evitar que 0 active fallbacks
        const nextAiTurnCount = data?.aiTurnCount ?? data?.state?.aiTurnCount ?? data?.sessionState?.aiTurnCount;
        const nextCompletedTurns = data?.completedTurns ?? nextAiTurnCount ?? 0;
        
        // P0-FIX: turnCount mapeado desde completedTurns (fuente de verdad oficial)
        // Backend V4 entrega completedTurns, frontend lo mapea como turnCount para UI
        const backendTurnCount =
          (typeof data?.turnCount === 'number') ? data.turnCount :
          (typeof data?.completedTurns === 'number') ? data.completedTurns :
          (typeof data?.state?.turnCount === 'number') ? data.state.turnCount :
          (typeof data?.sessionState?.turnCount === 'number') ? data.sessionState.turnCount :
          null;
        
        // P0-FIX: progressPercentage solo desde backend (0 es valor válido)
        const backendProgressPercentage =
          (typeof data?.progressPercentage === 'number') ? data.progressPercentage :
          (typeof data?.state?.progressPercentage === 'number') ? data.state.progressPercentage :
          null;
        
        // P0-FIX: maxTurns solo desde backend (evitar || porque 0 es válido)
        const backendMaxTurns =
          (typeof data?.maxTurns === 'number') ? data.maxTurns :
          (typeof data?.state?.maxTurns === 'number') ? data.state.maxTurns :
          null;
        
        // P0-FIX: currentTurn solo desde backend
        const backendCurrentTurn =
          (typeof data?.currentTurn === 'number') ? data.currentTurn :
          (typeof data?.state?.currentTurn === 'number') ? data.state.currentTurn :
          null;
        
        console.log('📊 [METRICS UPDATE] Datos del backend (determinísticos):', {
          completedTurns: nextCompletedTurns,
          aiTurnCount: nextAiTurnCount,
          turnCount: backendTurnCount,
          maxTurns: backendMaxTurns,
          progressPercentage: backendProgressPercentage,
          currentPhase: data.currentPhase,
          currentPhaseDisplayName: data.currentPhaseDisplayName,  // 🎟️ TCK-P1-PHASE-DISPLAYNAME-V4
          phaseHistoryLength: data.phaseHistory?.length
        });
        
        if (typeof nextAiTurnCount === 'number') {
          setAiTurnCount(nextAiTurnCount);
        }

        // ================== METRICS UPDATE (SIEMPRE PRIMERO) ==================
        // P0-FIX: Métricas se actualizan ANTES de cualquier freeze
        // Backend es la fuente de verdad → NUNCA bloquear actualización de métricas
        const hasModerationActive = data.moderatorAlert?.active === true;
        const nextMetrics = normalizeMetrics(data, sessionMetrics);

        console.log('EPL_V4_METRICS|BEFORE', {
          hasModerationActive,
          prev: sessionMetrics,
        });

        // ✅ ACTUALIZACIÓN CONSOLIDADA DE SESSIONMETRICS (SIEMPRE se ejecuta)
        setSessionMetrics(prev => {
          const merged = {
            ...prev,
            ...nextMetrics,
            sessionId: session.sessionId,
            friction: data.friction || prev?.friction || { currentLevel: 'low', enabled: false },
            interventions: data.interventions || prev?.interventions || { total: 0, critical: 0, pending: 0 },
            detectedIntent: data.detectedIntent || prev?.detectedIntent || null,
            
            // 🎯 P0-FIX: turnCount 100% desde backend (mapeado desde completedTurns)
            turnCount: backendTurnCount !== null ? backendTurnCount : (prev?.turnCount ?? null),
            
            totalPhases: data.totalPhases || agent?.stateMachine?.phases?.length || 0,
            phasesCompleted: data.phaseHistory?.length || data.phasesCompleted || prev?.phasesCompleted || 0,
            
            // 🎯 Campos de progreso basado en turnos (DE BACKEND - typeof seguro)
            completedTurns: nextCompletedTurns,
            aiTurnCount: nextAiTurnCount,
            
            // P0-FIX: maxTurns con typeof (0 es válido)
            maxTurns: backendMaxTurns !== null ? backendMaxTurns : (prev?.maxTurns ?? (agent?.maxTurns || 0)),
            
            // P0-FIX: currentTurn con typeof (0 es válido)
            currentTurn: backendCurrentTurn !== null ? backendCurrentTurn : (prev?.currentTurn ?? null),
            
            // P0-FIX: progressPercentage con typeof (0 es válido)
            progressPercentage: backendProgressPercentage !== null ? backendProgressPercentage : (prev?.progressPercentage ?? null),
            
            // 🎟️ TCK-P1-PHASE-DISPLAYNAME-V4: Nombre visual de fase para UI
            currentPhaseDisplayName: data.currentPhaseDisplayName ?? data.currentPhase ?? prev?.currentPhaseDisplayName ?? null,
            
            sessionStatus: data.sessionStatus || prev?.sessionStatus,
            tokenUsage: data.tokenUsage || prev?.tokenUsage || null
          };

          console.log('EPL_V4_METRICS|AFTER', merged);
          return merged;
        });

        // ================== MODERATION FREEZE (DESPUÉS DE MÉTRICAS) ==================
        // ⛔ IMPORTANTE: Freeze NO bloquea métricas (ya actualizadas arriba)
        // Solo congela efectos secundarios (audio, animaciones, scroll)
        if (hasModerationActive) {
          console.warn('EPL_V4_METRICS|FREEZE_ACTIVE', {
            completedTurns: nextCompletedTurns,
            sessionId: data.sessionId,
          });
          // ✅ Métricas ya actualizadas - solo saltar side-effects aquí
          // Continuar flujo sin autoplay/animaciones
        }
      } catch (metricsError) {
        console.error('❌ [METRICS UPDATE] Error actualizando métricas:', metricsError);
      }
      // ========== FIN ACTUALIZACIÓN DE MÉTRICAS ==========

      // Actualizar state machine si existe currentPhase
      if (data.currentPhase) {
        setCurrentPhase(data.currentPhase.id || data.currentPhase.phaseId || data.currentPhase);
        // Actualizar historial de fases desde backend
        if (data.phaseHistory) {
          setPhaseHistory(data.phaseHistory);
        }
      }

      // 🎫 TICKET P0 (15 ene 2026): Cierre por estado terminal, NO por maxTurns
      // Fuentes de verdad para cierre:
      // 1) data.sessionClosed === true (campo canónico)
      // 2) data.sessionStatus === 'armed_to_close' | 'completed' | 'closed'
      // 3) data.isTerminal === true
      
      const sessionClosed = data.sessionClosed === true;
      const isTerminal = data.isTerminal === true;
      const isTerminalStatus = ['armed_to_close', 'completed', 'closed'].includes(data.sessionStatus);
      
      // ✅ CANGENERATE EXAM: Cualquier indicador de cierre habilita el flujo
      const canGenerateExam = sessionClosed || isTerminal || isTerminalStatus;
      
      console.log('[SESSION_CLOSE_GATE]', {
        sessionClosed,
        isTerminal,
        sessionStatus: data.sessionStatus,
        isTerminalStatus,
        canGenerateExam,
        endReason: data.endReason,
        aiTurnCount: data.aiTurnCount,
        maxTurns: data.maxTurns,
        timestamp: Date.now()
      });
      
      // Actualizar estado de sesión desde backend
      if (data.sessionStatus) {
        setSessionStatus(data.sessionStatus);
        
        // 🎟️ TICKET P0: armed_to_close = fin lógico (puede ser antes de maxTurns)
        if (data.sessionStatus === 'armed_to_close') {
          setIsSessionComplete(canGenerateExam);
          
          // Mensaje adaptativo: cierre anticipado vs cierre normal
          const turnsCompleted = data.aiTurnCount || data.completedTurns || 0;
          const maxTurnsConfig = data.maxTurns || agent?.maxTurns || 0;
          const isEarlyClose = maxTurnsConfig > 0 && turnsCompleted < maxTurnsConfig;
          
          if (isEarlyClose) {
            const reason = data.endReason === 'STATE_MACHINE_END' ? 'state machine completó' :
                          data.endReason === 'MODERATION_TERMINATION' ? 'moderación' :
                          data.endReason === 'FORCED_BY_CONFIG' ? 'configuración' :
                          'el sistema';
            setCloseUiMessage(`La sesión concluyó en turno ${turnsCompleted}/${maxTurnsConfig} (${reason}). Ya puedes generar tu evaluación.`);
          } else {
            setCloseUiMessage('La simulación ha finalizado. Ya puedes generar tu evaluación.');
          }
        } 
        // completed = cerrado con evaluación
        else if (data.sessionStatus === 'completed') {
          setIsSessionComplete(true);
          if (data.message || data.ui?.message) {
            setCloseUiMessage(data.message || data.ui.message);
          }
        }
        // closed = cerrado sin evaluación (ej: moderación)
        else if (data.sessionStatus === 'closed') {
          setIsSessionComplete(true);
          setCloseUiMessage(data.message || 'La sesión ha sido cerrada.');
        }
      }
      
      // 🎫 TICKET P0: Si sessionClosed=true pero status no cambió, forzar isSessionComplete
      if (sessionClosed && !isTerminalStatus) {
        console.warn('[SESSION_CLOSE_INCONSISTENCY]', {
          sessionClosed: true,
          sessionStatus: data.sessionStatus,
          message: 'Backend indica sessionClosed=true pero status no es terminal'
        });
        setIsSessionComplete(true);
      }

      // ========== INICIO: FIX METRICAS PANEL V4 - ACTUALIZAR ESTADOS (4 ene 2026) ==========
      // Actualizar estados individuales con datos del backend
      if (data) {
        // Actualizar fase actual
        const newPhase = data.currentPhase?.id || data.currentPhase?.phaseId || data.currentPhase;
        if (newPhase) {
          setCurrentPhase(newPhase);
        }
        
        // Actualizar historial de fases
        if (data.phaseHistory && Array.isArray(data.phaseHistory)) {
          setPhaseHistory(data.phaseHistory);
        }
        
        // Nota: sessionStatus ya se procesó arriba (líneas 2167-2181) con lógica de armed_to_close
      }
      // ========== FIN: FIX METRICAS PANEL V4 - ACTUALIZAR ESTADOS ==========
      
      // Verificar si la sesión está completa (compatibilidad legacy)
      // Regla: armed_to_close NUNCA debe mapearse a completed.
      if (data.isComplete && data.sessionStatus !== 'armed_to_close') {
        setIsSessionComplete(true);
        setSessionStatus('completed');
        
        // ✅ FIX: Mostrar mensaje temporal si evaluación aún no está lista
        if (!data.evaluationReady) {
          const loadingMessage = {
            id: `evaluation-loading-${Date.now()}`,
            role: 'system',
            content: '⏳ Generando evaluación final... Por favor espera 2-3 segundos antes de hacer clic en "Ver Evaluación".',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, loadingMessage]);
        }
      }

      // 🛡️ [MODERACION_IA][PUNTO_3] NEUTRALIZADO - moderatorAlert ya fue seteado en punto canónico (línea ~1865)
      // Solo mantener logs de auditoría para verificar que el backend envió el dato
      console.log('[DEBUG][MOD_ALERT][RAW]', data.moderatorAlert, 'timestamp', Date.now());
      
      if ("moderatorAlert" in data) {
        console.log('🛡️ [MODERACION_IA][PUNTO_3] ✅ moderatorAlert presente - ya fue seteado en punto canónico');
        const normalized = normalizeModeratorAlert(data.moderatorAlert);
        console.log('[MOD_ALERT_NORMALIZED]', normalized);
      } else {
        console.log('🛡️ [MODERACION_IA][PUNTO_3] data.moderatorAlert NO presente');
      }
      // ❌ NO setear estado aquí - el moderador tiene prioridad alta y ya fue procesado

      // Actualizar contadores de ayuda desde backend
      if (data.helpRequestsCount !== undefined) {
        setHelpRequestsCount(data.helpRequestsCount);
      }
      if (data.lastHelpRequestTurn !== undefined) {
        setLastHelpRequestTurn(data.lastHelpRequestTurn);
      }

      // ========== LEGACY TTS DESHABILITADO - Ahora usa AUTO-PLAY SYSTEM ==========
      // TTS antiguo (Web Speech API del navegador) - REMOVIDO en favor del sistema P0
      // if (isTTSEnabled && 'speechSynthesis' in window) {
      //   const utterance = new SpeechSynthesisUtterance(data.response);
      //   utterance.lang = 'es-ES';
      //   window.speechSynthesis.speak(utterance);
      // }
      // ========== FIN LEGACY TTS ==========

    } catch (error) {
      // 🎟️ V4-P0-007: Manejo robusto de errores con correlationId tracking
      console.error('❌ [V4-P0-007] Error sending message:', error);
      
      const errorTimestamp = Date.now();
      const errorCorrelationId = `FE-ERROR-${errorTimestamp}`;
      
      // Log estructurado para debugging
      console.error('[SEND_MESSAGE_ERROR]', {
        correlationId: errorCorrelationId,
        sessionId: session.sessionId,
        error: error.message,
        stack: error.stack,
        timestamp: errorTimestamp
      });
      
      // Insertar mensaje de error en timeline con metadata completa
      const errorMessage = {
        id: `error-${errorTimestamp}`,
        role: 'system',
        speakerRole: 'SYSTEM',
        content: error.message || 'Error inesperado al enviar mensaje',
        timestamp: new Date(errorTimestamp),
        metadata: { 
          type: 'SYSTEM_ERROR',
          correlationId: errorCorrelationId,
          errorType: 'EXCEPTION'
        },
        isError: true,
        correlationId: errorCorrelationId
      };
      
      setMessages(prev => [...prev, errorMessage]);
      
      // 🧠 P0 ASSISTANTSTATE: Error en procesamiento → idle (desbloquear)
      setAssistantState('idle');
      console.log('🧠 [ASSISTANTSTATE] Error al procesar → idle (controles desbloqueados)');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Wrapper que usa la función del parent pero mantiene loading state local
  const handleConfirmOnboardingClick = async () => {
    if (!confirmOnboarding) {
      console.error('[VertexChatWindow] No confirmOnboarding prop provided');
      return;
    }

    try {
      setIsConfirmingOnboarding(true);
      await confirmOnboarding(); // Llamar a la función del parent
    } catch (error) {
      console.error('[VertexChatWindow] Error in confirmOnboarding:', error);
    } finally {
      setIsConfirmingOnboarding(false);
    }
  };

  // Confirmar onboarding y activar simulación (función local legacy - NO USAR)
  const confirmOnboardingLegacy = async () => {
    try {
      setIsConfirmingOnboarding(true);
      const token = getSafeToken();

      const res = await fetch(`${VERTEX_API_BASE}/v4/session/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId: session.sessionId
        })
      });

      const data = await res.json();

      console.log('[VertexChat] Respuesta de /v4/session/confirm:', JSON.stringify(data, null, 2));

      if (data.success) {
        // ✅ FLUJO CORRECTO V3: Cliente IA habla PRIMERO
        setSessionPhase('simulation_active');
        
        console.log('[VertexChat] Simulación activada con fase:', data.currentPhase);
        console.log('[VertexChat] Primer mensaje del cliente:', data.firstClientMessage?.content);
        
        // 📋 Guardar información del escenario para mostrar en MetricsPanel
        if (data.scenario) {
          setScenarioInfo({
            title: data.scenario.title || 'Escenario de práctica',
            description: data.scenario.description || ''
          });
          console.log('[VertexChat] 📋 Escenario guardado:', data.scenario.title);
        }
        
        // 🎟️ TCK-P1-V4-BRIEF-KICKOFF-UX-POLISH-AND-CONTRACT
        // NO insertar kickoff/primer mensaje localmente. Debe venir del backend como mensaje inicial.
        
        console.log('[VertexChat] Cliente IA generó primer mensaje. Usuario puede responder.');
      } else {
        console.error('[VertexChat] Error confirmando onboarding:', data.error);
      }
    } catch (error) {
      console.error('[VertexChat] Error en confirmación:', error);
    } finally {
      setIsConfirmingOnboarding(false);
    }
  };

  // Solicitar ayuda del coach (modo on-demand)
  const requestCoachHelp = async () => {
    // 🎫 7391-K P0: Guard de seguridad antes de ejecutar
    const currentTurn = sessionMetrics?.turnCount ?? 0;
    if (isCoachProcessing || isSending || currentTurn === 1) {
      console.warn('[requestCoachHelp] Bloqueado por guard:', {
        isCoachProcessing,
        isSending,
        currentTurn,
        reason: currentTurn === 1 ? 'TURNO 1 BLOQUEADO' : 'ESTADO INVÁLIDO'
      });
      return;
    }

    try {
      setIsCoachProcessing(true); // ← Usar estado específico del coach

      // Agregar mensaje temporal EN COLUMNA USUARIO (derecha) indicando análisis
      const tempAnalysisMessage = {
        id: `temp-analysis-${Date.now()}`,
        role: 'coach-analyzing', // Rol especial para spinner en columna usuario
        content: 'El coach está preparando tu feedback...',
        timestamp: new Date(),
        isTemporary: true
      };
      
      setMessages(prev => [...prev, tempAnalysisMessage]);

      const token = getSafeToken();

      const response = await fetch(`${VERTEX_API_BASE}/v4/session/help`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : undefined
        },
        body: JSON.stringify({
          sessionId: session.sessionId
        })
      });

      // 🎫 TCK-V4-FIX-015: Mejor manejo de errores HTTP
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[requestCoachHelp] ❌ HTTP Error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`HTTP ${response.status}: ${response.statusText || 'Endpoint no encontrado'}`);
      }

      const data = await response.json();
      
      console.log('[requestCoachHelp] 📥 RAW RESPONSE DATA:', JSON.stringify(data, null, 2));

      if (!data.success) {
        console.error('[requestCoachHelp] ❌ Backend returned success=false:', data);
        throw new Error(data.message || 'Error al solicitar ayuda');
      }

      console.log('[requestCoachHelp] 📥 Respuesta recibida:', data);

      // 💾 Actualizar tokenUsage SIEMPRE (fusionar con datos existentes)
      console.log('📊 [DEBUG request-help] tokenUsage del Coach recibido:', data.tokenUsage);
      setSessionMetrics(prev => ({
        ...prev,
        // NO actualizar progreso aquí - el Coach no incrementa turnos
        tokenUsage: data.tokenUsage || prev?.tokenUsage || null
      }));

      // Actualizar contador
      setHelpRequestsCount(data.helpRequestsCount || helpRequestsCount + 1);
      setLastHelpRequestTurn(data.turnCount ?? sessionMetrics?.turnCount ?? 0);

      console.log('[requestCoachHelp] 🔍 Verificando coachAlert:', {
        exists: !!data.coachAlert,
        hasSuggestion: data.coachAlert?.hasSuggestion,
        suggestion: data.coachAlert?.suggestion ? 'EXISTS' : 'NULL',
        suggestionText: data.coachAlert?.suggestion,
        type: data.coachAlert?.type,
        action: data.coachAlert?.action,
        fullCoachAlert: data.coachAlert
      });

      // Remover mensaje temporal
      const updatedMessages = messages.filter(msg => !msg.isTemporary);
      console.log('[requestCoachHelp] 🗑️ Mensajes después de filtrar temporales:', updatedMessages.length);
      
      // NUEVA PROPUESTA UX: Coach como mensaje independiente en columna usuario AL FINAL
      if (data.coachAlert && data.coachAlert.hasSuggestion) {
        console.log('[requestCoachHelp] ✅ CONDICIÓN CUMPLIDA - Creando mensaje coach en columna usuario...');
        
        const coachMessage = {
          id: `coach-${Date.now()}`,
          role: 'coach-user-column', // Rol especial para renderizado en columna usuario
          content: data.coachAlert.suggestion,
          timestamp: new Date(),
          coachAlert: {
            hasSuggestion: true,
            type: data.coachAlert.type || 'tip',
            suggestion: data.coachAlert.suggestion,
            reason: data.coachAlert.reason,
            action: data.coachAlert.action
          }
        };
        
        console.log('[requestCoachHelp] 📨 Coach mensaje creado para columna usuario:', coachMessage);
        
        // Añadir coach AL FINAL en columna usuario
        const finalMessages = [...updatedMessages, coachMessage];
        setMessages(finalMessages);
        console.log('[requestCoachHelp] ✅ Coach añadido en columna usuario. Total:', finalMessages.length);
        
      } else {
        console.log('[requestCoachHelp] ❌ CONDICIÓN NO CUMPLIDA - Razón:', {
          hasCoachAlert: !!data.coachAlert,
          hasSuggestion: data.coachAlert?.hasSuggestion,
          suggestionContent: data.coachAlert?.suggestion,
          coachAlertType: typeof data.coachAlert,
          coachAlertKeys: data.coachAlert ? Object.keys(data.coachAlert) : 'N/A'
        });
        setMessages([...updatedMessages]);
      }

    } catch (error) {
      console.error('Error requesting coach help:', error);
      // Remover mensaje temporal en caso de error
      setMessages(prev => prev.filter(msg => !msg.isTemporary));
      alert('No se pudo solicitar ayuda del coach. Intenta de nuevo.');
    } finally {
      setIsCoachProcessing(false); // ← Usar estado específico del coach
    }
  };

  // 🎫 TICKET P0 (15 ene 2026): canOfferEvaluation por estado terminal, NO por turnos
  // Condiciones:
  // 1) Estado terminal (armed_to_close/completed)
  // 2) Handler de evaluación disponible
  // 3) Evaluación habilitada en configuración del agente
  const canOfferEvaluation =
    (sessionStatus === 'armed_to_close' || sessionStatus === 'completed') &&
    !!(onGenerateEvaluation || onViewEvaluation) &&
    !!(agent?.evaluationCriteria?.enabled);

  console.log('[EVAL_GATE]', {
    sessionStatus,
    isSessionComplete,
    aiTurnCount,
    hasHandler: !!(onGenerateEvaluation || onViewEvaluation),
    evaluationEnabled: agent?.evaluationCriteria?.enabled,
    canOfferEvaluation
  });

  return (
    <div className="h-full flex flex-col min-[768px]:flex-row bg-gray-50">
      
      {/* 🔓 PROMPT DE UNLOCK AUDIO (Safari iOS / Edge móvil) */}
      {showAudioUnlockPrompt && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md min-[768px]:max-w-[90vw] min-[768px]:w-[80vw] min-[768px]:max-w-[920px] min-[768px]:p-8 min-[768px]:mx-auto p-6 relative animate-fade-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Volume2 className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Activar Audio
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Para reproducir audio automáticamente, necesitamos tu permiso. 
                Toca el botón para habilitar el sonido.
              </p>
            </div>
            
            <button
              id="audio-unlock-btn"
              onMouseDown={(e) => {
                console.log('🔥🔥🔥 [AUDIO] onMouseDown detectado');
                e.preventDefault();
              }}
              onTouchStart={(e) => {
                console.log('🔥🔥🔥 [AUDIO] onTouchStart detectado (móvil)');
                e.preventDefault();
              }}
              onClick={async () => {
                console.log('🔥 [AUDIO] onClick detectado - EJECUTANDO UNLOCK');
                
                // 🔧 Detectar iOS antiguo (iPhone 13, 14 = iOS 13-16) que pierde gesto con await
                // iOS 17+ FIX: UserAgent más genérico, usar múltiples métodos
                const hasIOSUA = /iPhone|iPad|iPod/.test(navigator.userAgent);
                const hasIOSPlatform = /iPhone|iPad|iPod|Macintosh/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                const isiOS = hasIOSUA || hasIOSPlatform;
                const iOSMatch = isiOS ? navigator.userAgent.match(/OS (\d+)_?(\d+)?/) : null;
                const iOSVersion = iOSMatch ? parseInt(iOSMatch[1]) : 0;
                const isOldIOS = isiOS && iOSVersion < 17; // iPhone 15 = iOS 17+
                
                console.log(`📱 [iOS FIX] iOS detectado: ${isiOS}, versión: ${iOSVersion}, legacy (< iOS 17): ${isOldIOS}`);
                
                // 🎯 iOS 13 FIX: Pre-cargar audio ANTES del await para preservar gesto
                if (isOldIOS && audioQueue.current.length > 0) {
                  const { cacheKey } = audioQueue.current[0];
                  const cached = audioCache.current[cacheKey];
                  
                  if (cached) {
                    console.log('🔄 [iOS 13 FIX] Pre-cargando audio en contexto del gesto...');
                    const audioUrl = `data:audio/mpeg;base64,${cached.audioBase64}`;
                    const preloadAudio = new Audio(audioUrl);
                    preloadAudio.playsInline = true;
                    preloadAudio.preload = 'auto';
                    preloadAudio.load();
                    
                    // Guardar referencia temporal
                    window.__iOS13PreloadedAudio = preloadAudio;
                    console.log('✅ [iOS 13 FIX] Audio pre-cargado, readyState:', preloadAudio.readyState);
                  }
                }
                
                try {
                  // FASE 1: Esperar unlock COMPLETO antes de continuar
                  console.log('⏳ [AUDIO] Esperando unlock completo...');
                  const unlocked = await unlockAudio();
                  
                  if (unlocked) {
                    console.log('✅ [AUDIO] Audio desbloqueado EXITOSAMENTE (verificado)');
                  } else {
                    console.warn('⚠️ [AUDIO] Unlock falló pero continuando...');
                  }
                  
                } catch (err) {
                  console.error('❌ [AUDIO] Error en unlock:', err);
                  // P0-FIX: NO procesar cola si unlock falló (evita loop infinito)
                  console.warn('⚠️ [AUDIO] Unlock falló - NO procesando cola para evitar loop');
                  return; // Salir sin cerrar modal ni procesar cola
                }
                
                // P0-FIX: Solo cerrar modal y procesar cola si unlock fue exitoso
                if (audioUnlockedRef.current) {
                  console.log('✅ [AUDIO] Unlock confirmado - cerrando modal y procesando cola');
                  setShowAudioUnlockPrompt(false);
                  
                  // FASE 1: Procesar cola DESPUÉS de confirmar unlock
                  // iOS 15+: await garantiza audioUnlockedRef = true
                  // iOS 13: Pre-carga preserva gesto del usuario
                  console.log('🎵 [AUDIO] Procesando cola (unlock confirmado)...');
                  await processAudioQueue();
                } else {
                  console.warn('⚠️ [AUDIO] audioUnlockedRef aún false después de unlock - manteniendo modal abierto');
                }
              }}
              style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-indigo-200/50 active:scale-95 cursor-pointer relative z-10"
              type="button"
            >
              <Volume2 className="w-5 h-5" />
              <span>Activar Audio</span>
            </button>
            
            <button
              onClick={() => setShowAudioUnlockPrompt(false)}
              className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
            >
              Continuar sin audio
            </button>
          </div>
        </div>
      )}
      
      {/* MODAL DE ONBOARDING (solo si phase=onboarding) */}
      {sessionPhase === 'onboarding' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg min-[768px]:max-w-[90vw] min-[768px]:w-[80vw] min-[768px]:max-w-[920px] min-[768px]:p-8 min-[768px]:mx-auto p-6 relative mx-auto">
              
              {/* Header Simplificado */}
              <div className="flex items-start gap-4 mb-6 border-b border-gray-100 pb-4">
                <div className="flex-shrink-0">
                  {agent.avatar && agent.avatar.startsWith('/') ? (
                    <img 
                      src={agent.avatar} 
                      alt="Avatar" 
                      className="w-12 h-12 rounded-xl object-cover shadow-sm border border-gray-100"
                    />
                  ) : (
                    <div className="text-3xl bg-indigo-50 w-12 h-12 rounded-xl flex items-center justify-center">{agent.avatar || '🤖'}</div>
                  )}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 leading-tight mb-1">
                    {session?.onboarding?.title || 'Simulación Interactiva'}
                  </h2>
                  <p className="text-sm text-gray-500 font-medium">
                    {agent.nombre}
                  </p>
                </div>
              </div>
              
              {/* Mensaje de onboarding - Foco Principal */}
              <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-5 mb-4">
                <h3 className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                  Tu Misión
                </h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {session?.onboarding?.message || 'En esta práctica pondrás a prueba tus habilidades ejecutivas con un cliente IA.'}
                </p>
              </div>
              
              {/* Términos y condiciones */}
              <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-4 mb-6 flex gap-3 items-start">
                <span className="text-lg mt-0.5">💡</span>
                <p className="text-xs text-amber-800 leading-relaxed">
                  {session?.onboarding?.consent || 'Tus respuestas serán evaluadas para generar métricas de desempeño.'}
                </p>
              </div>
              
              {/* Botones de acción */}
              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                <button
                  onClick={onCancelOnboarding || onExit}
                  className="w-full sm:w-auto px-6 py-3 text-gray-500 font-medium hover:text-gray-700 hover:bg-gray-50 rounded-xl transition-colors text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmOnboardingClick}
                  disabled={isConfirmingOnboarding}
                  className="flex-1 px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed transition-all shadow-lg shadow-indigo-200 flex items-center justify-center gap-2 text-sm"
                >
                  {isConfirmingOnboarding ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Preparando entorno...
                    </>
                  ) : (
                    <>
                      Iniciar Simulación
                      <ChevronLeft className="w-4 h-4 rotate-180" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* AREA DE CHAT */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header del agente - Optimizado para móvil */}
        <div className="bg-white border-b border-gray-200 px-3 py-2 md:px-4 md:py-3 flex items-center gap-2 md:gap-3 flex-shrink-0 shadow-sm z-10 relative">
          
          {/* Botón Atrás - Compacto */}
          <button
            onClick={() => {
              if (sessionPhase === 'simulation_active' && messages.length > 0) {
                setShowExitConfirmation(true);
              } else {
                exitSession();
              }
            }}
            className="p-1.5 -ml-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 text-gray-600"
            aria-label="Cerrar sesión"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Info Agente - Flexible y Truncado */}

          <div className="flex items-center flex-1 min-w-0 overflow-hidden">
            <h2 className="font-bold text-gray-900 text-sm md:text-base line-clamp-1 md:line-clamp-2 leading-tight">
              {agent.nombre}
            </h2>
          </div>

          {/* Botón métricas: visible siempre en modo compacto (tablet/móvil) */}
          {isCompact && (
            <button
              onClick={() => setShowMetricsDrawer(true)}
              className="p-2 rounded-lg transition-colors flex-shrink-0 bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
              aria-label="Ver métricas"
            >
              <BarChart2 className="w-5 h-5" />
            </button>
          )}

          {/* Controles (desktop) */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setIsTTSEnabled(!isTTSEnabled)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={isTTSEnabled ? 'Desactivar voz' : 'Activar voz'}
            >
              {isTTSEnabled ? (
                <Volume2 className="w-5 h-5 text-indigo-600" />
              ) : (
                <VolumeX className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>
        </div>


        {/* Drawer de métricas para tablet/móvil */}
        <MetricsDrawer
          open={showMetricsDrawer}
          onClose={() => setShowMetricsDrawer(false)}
          sessionMetrics={sessionMetrics}
          agent={agent}
          sessionStatusAfter={sessionStatusAfter}
          dataSource={{
            sessionStatusAfter,
            sessionStatusBefore: sessionStatus,
            status: session?.status
          }}
          moderatorAlert={moderatorAlert}
          currentPhase={currentPhase}
          currentPhaseDisplayName={currentPhaseDisplayName}
          turnsInPhase={turnsInPhase}
          isAdmin={isAdmin}
        />

        {/* Contenedor Chat + Input (si drawer abierto, ocultar chat en compacto) */}
        <div className={`flex-1 flex flex-col min-h-0 overflow-hidden supports-[height:100dvh]:min-h-0 ${isCompact && showMetricsDrawer ? 'hidden' : 'flex'}`}>
          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
            {/* BRIEF PEDAGÓGICO V4 - Dispositivo de entrenamiento ejecutivo */}
            {session?.briefing && (
              <div className="flex flex-col space-y-2">
                <BriefPedagogico briefing={session.briefing} />
                
                {/* ========== P0: CONTROLES DE AUDIO PARA BRIEF ========== */}
                <div className="flex items-center gap-2 ml-4">
                  {/* Indicador de estado del audio */}
                  {audioStates['brief-session-briefing']?.status === 'generating' && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Generando audio...</span>
                    </div>
                  )}
                  
                  {audioStates['brief-session-briefing']?.status === 'playing' && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-700 font-medium">
                      <Volume2 className="w-3.5 h-3.5 animate-pulse" />
                      <span>Reproduciendo</span>
                    </div>
                  )}
                  
                  {/* 🔓 Badge "Activar Audio" - cuando autoplay bloqueado */}
                  {/* TICKET AUD-P0-13: Condición única estable (no transitoria) */}
                  {!audioUnlockedRef.current && 
                   audioStates['brief-session-briefing']?.needsUnlock && 
                   audioStates['brief-session-briefing']?.status === 'blocked' && (
                    <button
                      type="button"
                      onClick={(e) => {
                        console.log('[CTA] click activar audio', { messageId: 'brief-session-briefing' });
                        // P0 CHECK: Verificar unlockAudio en badge CTA
                        console.log('[AUDIO][UNLOCK_CLICK] badge CTA brief - typeof unlockAudio:', typeof unlockAudio);
                        // AUD-P1-21: Abrir modal en lugar de llamar unlockAudio directamente
                        // AUD-P1-22: Guardar mensaje pendiente en refs
                        pendingMessageIdRef.current = 'brief-session-briefing';
                        pendingCacheKeyRef.current = audioStates['brief-session-briefing']?.cacheKey || null;
                        setShowAudioUnlockPrompt(true);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 
                        bg-indigo-600 hover:bg-indigo-700 
                        text-white text-xs font-semibold
                        rounded-full shadow-lg shadow-indigo-200/50
                        transition-all duration-200 hover:scale-105 active:scale-95 animate-pulse"
                      style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10 }}
                      title="Toca para activar audio"
                    >
                      <Volume2 className="w-3.5 h-3.5" />
                      <span>Activar Audio</span>
                    </button>
                  )}
                  
                  {/* Badge "Repetir" - visible cuando audio existe en cache */}
                  {audioStates['brief-session-briefing']?.cacheKey && 
                   audioCache.current[audioStates['brief-session-briefing'].cacheKey] && 
                   audioStates['brief-session-briefing']?.status !== 'playing' &&
                   !audioStates['brief-session-briefing']?.needsUnlock && (
                    <button
                      onClick={() => {
                        const cacheKey = audioStates['brief-session-briefing'].cacheKey;
                        playAudioFromCache(cacheKey, 'brief-session-briefing');
                      }}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 
                        bg-gray-100 hover:bg-gray-200 
                        text-gray-700 text-xs font-medium
                        rounded-full border border-gray-300
                        transition-all duration-200 hover:scale-105 active:scale-95"
                      title="Repetir audio del contexto"
                    >
                      <Volume2 className="w-3 h-3" />
                      <span>Repetir</span>
                    </button>
                  )}
                </div>
                {/* ========== FIN P0: AUDIO BRIEF ========== */}
              </div>
            )}
            
            {messages
              .filter(msg => msg.role !== 'system' && !msg.isBriefing)
              .filter(shouldRenderChatBubble)
              .map((msg, index) => {
              const brief = isBriefMessage(msg);

              // Regla UI: IA_CLIENTE = assistant/agent + (metadata.type kickoff/AI_RESPONSE) o fallback sender agent (y no coach/brief)
              const roleIsAssistantLike = msg?.role === 'assistant' || msg?.role === 'agent';
              const isKickoff = msg?.metadata?.type === 'SYSTEM_KICKOFF';
              const isAIResponse = msg?.metadata?.type === 'AI_RESPONSE';
              const isCoach = msg?.role === 'coach' || msg?.speakerRole === 'COACH' || msg?.metadata?.type === 'AI_COACH';
              const isClientMessage = roleIsAssistantLike && !brief && !isCoach && (isKickoff || isAIResponse || msg?.sender === 'agent');

              // Avatar viene desde DB (ruta tipo "/artemis.png"). No usar hardcode.
              const agentAvatarUrl =
                agent?.avatar && typeof agent.avatar === 'string' && agent.avatar.startsWith('/')
                  ? agent.avatar
                  : null;

              // Renderizar Brief premium o mensaje normal con burbujas
              return (
                <div key={msg._id || index}>
                  {brief ? (
                    <div className="flex flex-col">
                      {renderBriefMessage(msg)}
                      
                      {/* ========== P0: CONTROLES DE AUDIO PARA BRIEF ========== */}
                      {msg.content && msg.content.trim() && (
                        <div className="flex items-center gap-2 mt-2 ml-4">
                          {/* Indicador de estado del audio */}
                          {audioStates[msg.id]?.status === 'generating' && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Generando audio...</span>
                            </div>
                          )}
                          
                          {audioStates[msg.id]?.status === 'playing' && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-700 font-medium">
                              <Volume2 className="w-3.5 h-3.5 animate-pulse" />
                              <span>Reproduciendo</span>
                            </div>
                          )}
                          
                          {/* 🔓 Badge "Activar Audio" - cuando autoplay bloqueado */}
                          {/* TICKET AUD-P0-13: Condición única estable (no transitoria) */}
                          {!audioUnlockedRef.current && 
                           audioStates[msg.id]?.needsUnlock && 
                           audioStates[msg.id]?.status === 'blocked' && (
                            <button
                              type="button"
                              onClick={(e) => {
                                console.log('[CTA] click activar audio', { messageId: msg.id });
                                // P0 CHECK: Verificar unlockAudio en badge CTA
                                console.log('[AUDIO][UNLOCK_CLICK] badge CTA msg - typeof unlockAudio:', typeof unlockAudio);
                                // AUD-P1-21: Abrir modal en lugar de llamar unlockAudio directamente
                                // AUD-P1-22: Guardar mensaje pendiente en refs
                                pendingMessageIdRef.current = msg.id;
                                pendingCacheKeyRef.current = audioStates[msg.id]?.cacheKey || null;
                                setShowAudioUnlockPrompt(true);
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 
                                bg-indigo-600 hover:bg-indigo-700 
                                text-white text-xs font-semibold
                                rounded-full shadow-lg shadow-indigo-200/50
                                transition-all duration-200 hover:scale-105 active:scale-95 animate-pulse"
                              style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10 }}
                              title="Toca para activar audio"
                            >
                              <Volume2 className="w-3.5 h-3.5" />
                              <span>Activar Audio</span>
                            </button>
                          )}
                          
                          {/* Badge "Repetir" - visible cuando audio existe en cache */}
                          {audioStates[msg.id]?.cacheKey && audioCache.current[audioStates[msg.id].cacheKey] && 
                           audioStates[msg.id]?.status !== 'playing' && !audioStates[msg.id]?.needsUnlock && (
                            <button
                              onClick={() => {
                                const cacheKey = audioStates[msg.id].cacheKey;
                                playAudioFromCache(cacheKey, msg.id);
                              }}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 
                                bg-gray-100 hover:bg-gray-200 
                                text-gray-700 text-xs font-medium
                                rounded-full border border-gray-300
                                transition-all duration-200 hover:scale-105 active:scale-95"
                              title="Repetir audio"
                            >
                              <Volume2 className="w-3 h-3" />
                              <span>Repetir</span>
                            </button>
                          )}
                        </div>
                      )}
                      {/* ========== FIN P0: AUDIO BRIEF ========== */}
                    </div>
                  ) : msg.role === 'coach-analyzing' ? (
                    // SPINNER ANÁLISIS COACH - En columna usuario (derecha)
                    <div className="flex justify-end">
                      <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 border-2 border-amber-200 rounded-2xl shadow-sm">
                        <Loader2 className="w-4 h-4 text-amber-600 animate-spin flex-shrink-0" />
                        <span className="text-sm font-medium text-amber-900">{msg.content}</span>
                      </div>
                    </div>
                  ) : msg.role === 'coach-user-column' ? (
                    // COACH EN COLUMNA USUARIO - Renderizar como CoachHintBubble alineado a la derecha
                    <div className="flex justify-end">
                      <div className="max-w-[80%] md:max-w-[60%]">
                        <CoachHintBubble 
                          coachAlert={msg.coachAlert}
                          messageId={msg._id || msg.id}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                      {/* Micro-label kickoff (sutil, encima de la burbuja, no dentro del texto) */}
                      {isKickoff && isClientMessage && (
                        <div
                          className="text-xs text-gray-500 font-medium mb-1 ml-[calc(2rem+0.5rem)] md:ml-[calc(2.25rem+0.5rem)]"
                          aria-hidden="true"
                        >
                          Pregunta inicial del cliente
                        </div>
                      )}

                      <div className={`flex items-start gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {/* Avatar IA_CLIENTE (incluye KICKOFF y AI_RESPONSE) - con fallback */}
                        {isClientMessage && (
                          agentAvatarUrl ? (
                            <img
                              src={agentAvatarUrl}
                              alt="Avatar"
                              className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-gray-200 object-cover flex-shrink-0"
                            />
                          ) : (
                            <div
                              className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-gray-200 bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-semibold flex-shrink-0"
                              aria-hidden="true"
                            >
                              {getAvatarFallbackText(agent?.nombre)}
                            </div>
                          )
                        )}

                        <div
                          className={`w-auto min-w-[4.75rem] max-w-[80%] md:max-w-[60%] px-4 py-3 rounded-2xl ${
                            msg.role === 'user'
                              ? 'bg-indigo-600 text-white'
                              : msg.role === 'system'
                              ? msg.isError ? '' : 'bg-red-50 text-red-800 border border-red-200' // 🎟️ V4-P0-007: Errors use ErrorDisplay
                              : msg.role === 'coach' || msg.speakerRole === 'COACH'
                              ? 'bg-amber-50 text-amber-900 border border-amber-300 shadow-sm'
                              : 'bg-white text-gray-900 border border-gray-200 shadow-sm'
                          } ${msg.role === 'user' && shouldUseComfortMinWidth(msg) ? 'min-w-[10rem]' : ''}`}
                          aria-label={isKickoff ? 'Pregunta inicial del cliente' : undefined}
                        >
                          {/* 🎟️ V4-P0-007: Render ErrorDisplay for error messages */}
                          {msg.isError && msg.correlationId ? (
                            <ErrorDisplay
                              error={msg.content}
                              correlationId={msg.correlationId}
                              timestamp={msg.timestamp ? msg.timestamp.getTime() : Date.now()}
                              variant="inline"
                              userMessage="Hubo un error al procesar tu mensaje. Por favor, verifica tu conexión e intenta nuevamente."
                            />
                          ) : (
                            <div className="text-sm leading-relaxed">
                              {renderMarkdownText(msg.content)}
                            </div>
                          )}

                          {/* ========== P0: INDICADOR + BADGE REPETIR ========== */}
                          {isClientMessage && msg.content && msg.content.trim() && (
                            <div className="flex items-center gap-2 mt-2">
                              {/* Indicador de estado del audio */}
                              {audioStates[msg.id]?.status === 'generating' && (
                                <div className="flex items-center gap-1.5 text-xs text-indigo-600">
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  <span>Generando audio...</span>
                                </div>
                              )}
                              
                              {audioStates[msg.id]?.status === 'playing' && (
                                <div className="flex items-center gap-1.5 text-xs text-indigo-600 font-medium">
                                  <Volume2 className="w-3.5 h-3.5 animate-pulse" />
                                  <span>Reproduciendo</span>
                                </div>
                              )}
                              
                              {/* 🔓 Badge "Activar Audio" - cuando autoplay bloqueado */}
                              {/* TICKET AUD-P0-13: Condición única estable (no transitoria) */}
                              {!audioUnlockedRef.current && 
                               audioStates[msg.id]?.needsUnlock && 
                               audioStates[msg.id]?.status === 'blocked' && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    console.log('[CTA] click activar audio', { messageId: msg.id });
                                    unlockAudio();
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 
                                    bg-indigo-600 hover:bg-indigo-700 
                                    text-white text-xs font-semibold
                                    rounded-full shadow-lg shadow-indigo-200/50
                                    transition-all duration-200 hover:scale-105 active:scale-95 animate-pulse"
                                  style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10 }}
                                  title="Toca para activar audio"
                                >
                                  <Volume2 className="w-3.5 h-3.5" />
                                  <span>Activar Audio</span>
                                </button>
                              )}
                              
                              {/* Badge "Repetir" - visible cuando audio existe en cache */}
                              {audioStates[msg.id]?.cacheKey && audioCache.current[audioStates[msg.id].cacheKey] && 
                               audioStates[msg.id]?.status !== 'playing' && !audioStates[msg.id]?.needsUnlock && (
                                <button
                                  onClick={() => {
                                    const cacheKey = audioStates[msg.id].cacheKey;
                                    playAudioFromCache(cacheKey, msg.id);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 
                                    bg-indigo-50 hover:bg-indigo-100 
                                    text-indigo-700 text-xs font-medium
                                    rounded-full border border-indigo-200
                                    transition-all duration-200 hover:scale-105 active:scale-95"
                                  title="Repetir audio"
                                >
                                  <Volume2 className="w-3 h-3" />
                                  <span>Repetir</span>
                                </button>
                              )}
                            </div>
                          )}
                          {/* ========== FIN P0 ========== */}

                          <p className={`text-xs mt-1 whitespace-nowrap text-right ${
                            msg.role === 'user' 
                              ? 'text-indigo-200' 
                              : msg.role === 'coach' || msg.speakerRole === 'COACH'
                              ? 'text-amber-600'
                              : 'text-gray-500'
                          }`}>
                            {msg.timestamp.toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      
                      {/* Coach Hint Bubble - DENTRO del contenedor del mensaje del usuario */}
                      {msg.role === 'user' && msg.coachAlert && msg.coachAlert.hasSuggestion && (
                        <CoachHintBubble 
                          coachAlert={msg.coachAlert}
                          messageId={msg._id || msg.id}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          
          {/* Typing Indicator IA Cliente - Solo cuando IA está pensando, NO cuando es el coach */}
          {isSending && !isCoachProcessing && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 shadow-sm">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          
          {/* Coach Help Pill - Visible cuando mode es 'on-demand' o 'hybrid' */}
          {(() => {
            const coachMode = agent?.coachConfig?.mode || 'on-demand';
            
            // 🔍 LÓGICA CORREGIDA: Mostrar solo cuando último mensaje es de la IA
            const lastMessage = messages[messages.length - 1];
            const lastMessageIsAgent = lastMessage?.role === 'agent' || lastMessage?.role === 'assistant';
            const lastMessageIsCoach = lastMessage?.role === 'coach-user-column';
            const lastMessageIsAnalyzing = lastMessage?.role === 'coach-analyzing';
            
            // 🎫 7391-K P0: BLOQUEAR TURNO 1 - Backend cuenta turnos desde 1
            const currentTurn = sessionMetrics?.turnCount ?? 0;
            const isFirstTurn = currentTurn === 1;
            

            // Política V4: solo mostrar botón Help si aiTurnCount >= 2
            const shouldShowButton = !isSessionComplete &&
              (coachMode === 'on-demand' || coachMode === 'hybrid') &&
              messages.length >= 2 &&
              aiTurnCount >= 2 && // ← REGLA ESTRICTA
              lastMessageIsAgent &&
              !lastMessageIsCoach &&
              !lastMessageIsAnalyzing &&
              !isSending &&
              !isCoachProcessing;
            
            console.log('[CoachHelpPill Debug]', {
              isSessionComplete,
              coachMode,
              messagesLength: messages.length,
              currentTurn,  // 🎫 7391-K P0
              isFirstTurn,  // 🎫 7391-K P0
              lastMessageRole: lastMessage?.role,
              lastMessageIsAgent,
              lastMessageIsCoach,
              lastMessageIsAnalyzing,
              isSending,
              shouldShowButton,
              agentName: agent?.nombreVisible,
              sessionStatus
            });
            
            return shouldShowButton && (
              <div className="flex justify-end px-4 mt-3">
                <CoachHelpPill
                  onClick={() => {
                    // Handler defensivo: no disparar request si aiTurnCount < 2
                    if (aiTurnCount < 2) return;
                    requestCoachHelp();
                  }}
                  helpRequestsCount={helpRequestsCount}
                  maxHelpRequests={agent?.coachConfig?.maxHelpRequests || maxHelpRequests}
                  disabled={isSending || isCoachProcessing || sessionStatus !== 'active'}
                  cooldownActive={
                    sessionMetrics?.turnCount && lastHelpRequestTurn
                      ? (sessionMetrics.turnCount - lastHelpRequestTurn) < (agent?.coachConfig?.helpCooldown || helpCooldown)
                      : false
                  }
                  shouldShowButton={shouldShowButton}
                  isSending={isSending}
                  lastMessageIsAnalyzing={lastMessageIsAnalyzing}
                />
              </div>
            );
          })()}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 bg-white border-t border-gray-200 p-2 md:p-4" style={{ paddingBottom: 'max(0.5rem, calc(0.5rem + env(safe-area-inset-bottom)))' }}>
          
          {/* Alerta del Moderador */}
          <ModeratorAlert 
            moderatorAlert={moderatorAlert}
            onDismiss={() => {
              setModeratorAlert({ active: false });
              // Enfocar input para que el usuario continúe escribiendo
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            onContinue={async () => {
              try {
                const token = getSafeToken();
                const response = await fetch(`${VERTEX_API_BASE}/v4/session/resume`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ sessionId: session.sessionId })
                });

                const data = await response.json();

                if (data.success) {
                  setModeratorAlert({ active: false });
                  setSessionStatus('active');
                  setIsSessionComplete(false); // ← CRÍTICO: habilitar input nuevamente
                  console.log('[V3] Sesión reanudada:', data.sessionId);
                } else {
                  console.error('[V3] Error al reanudar:', data.message);
                  alert(data.message || 'No se pudo reanudar la sesión');
                }
              } catch (error) {
                console.error('[V3] Error en resume:', error);
                alert('Error de conexión al reanudar sesión');
              }
            }}
            onClose={() => {
              console.log('[V3] Usuario cerró sesión desde moderator alert');
              onExit(); // Vuelve al dashboard de agentes
            }}
            onRequestHelp={requestCoachHelp}
          />

          {/* Fin lógico de simulación (V4): armado para cierre, salida explícita del usuario */}
          {sessionStatus === 'armed_to_close' && !isSessionComplete && (
            <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">
                    Sesión finalizada
                  </p>
                  <p className="text-xs text-amber-800">
                    {canOfferEvaluation 
                      ? 'Puedes generar la evaluación o presiona X para salir.'
                      : 'Sesión finalizada. Presiona X para salir.'
                    }
                  </p>
                </div>

                {canOfferEvaluation && (
                  <button
                    onClick={async () => {
                      const handler = onGenerateEvaluation || onViewEvaluation;
                      if (!handler || isGeneratingEvaluationCta) return;
                      setIsGeneratingEvaluationCta(true);
                      try {
                        await handler(session.sessionId);
                      } finally {
                        setIsGeneratingEvaluationCta(false);
                      }
                    }}
                    disabled={isGeneratingEvaluationCta}
                    className="ml-3 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 flex-shrink-0"
                  >
                    {isGeneratingEvaluationCta ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      'Generar evaluación'
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Botón "Ver Evaluación" si la sesión está completa */}
          {isSessionComplete && onViewEvaluation && (
            <div className="mb-3 bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-green-900">
                    ✅ Sesión completada
                  </p>
                  <p className="text-xs text-green-700">
                    {closeUiMessage ? closeUiMessage : 'Tu evaluación está lista'}
                  </p>
                </div>
                <button
                  onClick={() => onViewEvaluation(session.sessionId)}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  Ver Evaluación
                </button>
              </div>
            </div>
          )}
          </div>

          {/* 🎤 P0: VOICE INPUT UX - Sistema integrado */}
          <div className="bg-white px-4 py-3">
            <VoiceInputUX
              inputValue={inputText}
              onInputChange={setInputText}
              onSend={sendMessage}
              onVoiceSend={(text) => {
                // Auto-send directo sin pasar por input
                const userMessage = {
                  id: Date.now(),
                  role: 'user',
                  content: text,
                  timestamp: new Date()
                };
                setMessages(prev => [...prev, userMessage]);
                setIsSending(true);
                
                // 🧠 P0 ASSISTANTSTATE: IA toma el turno - BLOQUEO TOTAL (mismo que sendMessage)
                setAssistantState('thinking');
                console.log('🧠 [ASSISTANTSTATE][VOICE] Usuario envió mensaje de voz → thinking (input/mic/send bloqueados)');
                
                // Marca interacción real
                hadRealInteractionRef.current = true;
                
                // Enviar al backend
                const token = getSafeToken();
                fetch(apiEndpoint, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : undefined
                  },
                  body: JSON.stringify({
                    sessionId: session.sessionId,
                    message: text
                  })
                })
                .then(res => res.json())
                .then(data => {
                  // ========== PROCESAMIENTO IDÉNTICO A sendMessage() ==========
                  console.log('🔍 [VOICE_SEND] Response completa del backend:', data);
                  
                  if (!data.success) {
                    console.error('❌ [VOICE_SEND] Error del backend:', data.error);
                    
                    // 🧠 P0 ASSISTANTSTATE: Error backend (voz) → idle (desbloquear)
                    setAssistantState('idle');
                    console.log('🧠 [ASSISTANTSTATE][VOICE] Error backend → idle (controles desbloqueados)');
                    
                    // Manejar sesión pausada
                    if (data.requiresResume || data.sessionStatus === 'paused') {
                      setSessionStatus('paused');
                      // 🛡️ P0-MODERATOR-NORMALIZATION: Normalizar antes de setear
                      if ("moderatorAlert" in data) {
                        const normalized = normalizeModeratorAlert(data.moderatorAlert);
                        if (normalized?.active) {
                          console.log('🛡️ [MODERACION_IA][PUNTO_4_VOICE] Seteando alerta normalizada:', normalized);
                          setModeratorAlert(normalized);
                          console.log('[MOD_ALERT_SET_CALLED] PUNTO_4_VOICE - setModeratorAlert(normalized)');
                        } else {
                          console.log('🛡️ [MODERACION_IA][PUNTO_4_VOICE] Backend envió active:false, limpiando');
                          setModeratorAlert({ active: false });
                          console.log('[MOD_ALERT_SET_CALLED] PUNTO_4_VOICE - setModeratorAlert({active:false})');
                        }
                      } else {
                        // Backend no envió moderatorAlert, crear fallback local
                        setModeratorAlert({
                          active: true,
                          type: 'warning',
                          action: 'pause_session',
                          message: data.message || 'La sesión está pausada. Debes reanudarla para continuar.',
                          severity: 'medium'
                        });
                      }
                      return;
                    }
                    
                    // Manejar sesión completada
                    if (data.sessionStatus === 'completed') {
                      // 🛡️ P0-MODERATOR-NORMALIZATION: Normalizar antes de setear
                      if ("moderatorAlert" in data) {
                        const normalized = normalizeModeratorAlert(data.moderatorAlert);
                        if (normalized?.active) {
                          console.log('🛡️ [MODERACION_IA][PUNTO_5_VOICE] Seteando alerta normalizada:', normalized);
                          setModeratorAlert(normalized);
                          console.log('[MOD_ALERT_SET_CALLED] PUNTO_5_VOICE - setModeratorAlert(normalized)');
                        } else {
                          console.log('🛡️ [MODERACION_IA][PUNTO_5_VOICE] Backend envió active:false, limpiando');
                          setModeratorAlert({ active: false });
                          console.log('[MOD_ALERT_SET_CALLED] PUNTO_5_VOICE - setModeratorAlert({active:false})');
                        }
                      }
                      // Si data no trae moderatorAlert, NO tocar el estado (preservar alerta activa)
                      setSessionStatus('completed');
                      return;
                    }
                    
                    // Mostrar error
                    setMessages(prev => [
                      ...prev,
                      {
                        id: Date.now() + 1,
                        role: 'system',
                        content: `Error: ${data.error || data.message || 'No se pudo enviar el mensaje'}`,
                        timestamp: new Date()
                      }
                    ]);
                    return;
                  }
                  
                  // Procesar múltiples mensajes con roles separados (MISMO FLUJO QUE sendMessage)
                  const newMessages = [];
                  
                  if (data.text) {
                    // SOPORTE V3: Respuesta simple
                    const trimmed = normalizeComparableText(data.text);
                    if (isUsefulText(trimmed) && !isTimeOnlyText(trimmed)) {
                      newMessages.push({
                        id: Date.now() + 1,
                        role: 'agent',
                        speakerRole: 'CLIENT',
                        content: trimmed,
                        timestamp: new Date(),
                        metadata: { type: 'AI_RESPONSE' }
                      });
                    }
                  } else if (data.messages && Array.isArray(data.messages)) {
                    // Backend envía array de mensajes con speakerRole
                    for (const msg of data.messages) {
                      const msgText = normalizeComparableText(msg?.text);
                      if (isUsefulText(msgText) && !isTimeOnlyText(msgText)) {
                        newMessages.push({
                          id: Date.now() + Math.random(),
                          role: msg.speakerRole === 'COACH' ? 'coach' : 'agent',
                          speakerRole: msg.speakerRole,
                          content: msgText,
                          timestamp: msg?.timestamp ? new Date(msg.timestamp) : new Date(),
                          metadata: msg?.metadata?.type
                            ? { ...(msg.metadata || {}) }
                            : (msg.speakerRole === 'COACH' ? { type: 'AI_COACH' } : { type: 'AI_RESPONSE' }),
                          payload: msg?.payload
                        });
                      }
                    }
                  } else {
                    // 🎟️ P0-MODERATION-FILTER: NO crear fallback si moderación está activa
                    const hasModerationActive = data.moderatorAlert?.active === true;
                    
                    if (hasModerationActive) {
                      console.log('[P0-MODERATION-FILTER][VOICE] Fallback bloqueado - moderación activa');
                      filteredEmptyMessagesCount += 1;
                    } else {
                      // Fallback: formato antiguo
                      const trimmed = normalizeComparableText(data.response || data.message);
                      if (isUsefulText(trimmed) && !isTimeOnlyText(trimmed)) {
                        newMessages.push({
                          id: Date.now() + 1,
                          role: 'agent',
                          speakerRole: 'CLIENT',
                          content: trimmed,
                          timestamp: new Date(),
                          metadata: { type: 'AI_RESPONSE' }
                        });
                      }
                    }
                  }
                  
                  // Agregar mensajes al chat
                  setMessages(prev => [...prev, ...newMessages]);
                  
                  // 🧠 P0 ASSISTANTSTATE: IA terminó respuesta (voz) → idle (desbloquear)
                  setAssistantState('idle');
                  console.log('🧠 [ASSISTANTSTATE][VOICE] IA respondió → idle (controles desbloqueados)');
                  
                  // ========== P0: AUTO-PLAY AUDIO ==========
                  // Disparar generación de audio para cada mensaje del agente (paralelo)
                  newMessages.forEach(msg => {
                    // Detectar mensajes reproducibles (igual que en processMessages)
                    const isAgentMessage = (msg.role === 'agent' || msg.role === 'assistant' || msg.role === 'model') && 
                                          msg.speakerRole !== 'COACH' &&
                                          msg.content && msg.content.trim();
                    
                    const isBrief = isBriefMessage(msg);
                    const isKickoff = msg?.metadata?.type === 'SYSTEM_KICKOFF';
                    
                    const shouldAutoPlay = isAgentMessage || isBrief || isKickoff;
                    
                    if (shouldAutoPlay && msg.content && msg.content.trim()) {
                      const messageId = msg.id || msg._id || `msg-${Date.now()}-${Math.random()}`;
                      console.log('🎵 [VOICE_SEND] Disparando auto-play para mensaje:', messageId);
                      generateAndPlayAudio(msg.content, messageId, msg); // ← Pasar mensaje completo para Voice Roles
                    }
                  });
                  // ========== FIN P0: AUTO-PLAY ==========
                  
                  // Actualizar métricas - P0-DETERMINISTIC: Solo desde backend (sin cálculos locales)
                  if (data.completedTurns !== undefined || data.sessionStatus || data.turnCount !== undefined) {
                    setSessionMetrics(prev => ({
                      ...prev,
                      completedTurns: data.completedTurns ?? prev?.completedTurns,
                      // P0-FIX: aiTurnCount con ?? (0 es válido), no ||
                      aiTurnCount: data.aiTurnCount ?? data.completedTurns ?? prev?.aiTurnCount,
                      // P0-FIX: turnCount solo desde backend (coherente con sendMessage)
                      turnCount: data.turnCount ?? prev?.turnCount,
                      sessionStatus: data.sessionStatus || prev?.sessionStatus
                    }));
                  }
                  
                  // Actualizar estado de sesión
                  if (data.sessionStatus) {
                    setSessionStatus(data.sessionStatus);
                    if (data.sessionStatus === 'completed') {
                      setIsSessionComplete(true);
                    }
                  }
                  
                  // ========== CRÍTICO: MANEJAR ALERTAS DEL MODERADOR ==========
                  // 🛡️ [MODERACION_IA][PUNTO_6_VOICE] Manejar alertas con normalización
                  if ("moderatorAlert" in data) {
                    console.log('🛡️ [MODERACION_IA][PUNTO_6_VOICE] data.moderatorAlert PRESENTE, normalizando...');
                    const normalized = normalizeModeratorAlert(data.moderatorAlert);
                    console.log('[MOD_ALERT_NORMALIZED][VOICE]', normalized);
                    
                    if (normalized?.active === true) {
                      console.log('🛡️ [MODERACION_IA][PUNTO_6_VOICE] ✅ moderatorAlert ACTIVO, seteando estado');
                      setModeratorAlert(normalized);
                      console.log('[MOD_ALERT_SET_CALLED] PUNTO_6_VOICE - setModeratorAlert(normalized) con active:true');
                    } else if (normalized?.active === false) {
                      console.log('🛡️ [MODERACION_IA][PUNTO_6_VOICE] ⏭️ Backend dice active:false EXPLÍCITO, limpiando estado');
                      setModeratorAlert({ active: false });
                      console.log('[MOD_ALERT_SET_CALLED] PUNTO_6_VOICE - setModeratorAlert({active:false})');
                    } else {
                      console.log('🛡️ [MODERACION_IA][PUNTO_6_VOICE] ⚠️ Normalización retornó null o sin active, NO tocar estado');
                    }
                  } else {
                    console.log('🛡️ [MODERACION_IA][PUNTO_6_VOICE] data.moderatorAlert NO presente, NO tocar estado (preservar activo)');
                  }
                  // ========== FIN MODERATOR ALERT ==========
                  
                  // ========== CRÍTICO: ACTUALIZAR CONTADORES DE AYUDA DEL COACH ==========
                  // Faltaba sincronizar estos contadores después de mensajes de voz
                  if (data.helpRequestsCount !== undefined) {
                    setHelpRequestsCount(data.helpRequestsCount);
                  }
                  if (data.lastHelpRequestTurn !== undefined) {
                    setLastHelpRequestTurn(data.lastHelpRequestTurn);
                  }
                  // ========== FIN CONTADORES COACH ==========
                })
                .catch(error => {
                  console.error('❌ [VOICE_UX_P0] Error de red:', error);
                  setMessages(prev => [
                    ...prev,
                    {
                      id: Date.now() + 1,
                      role: 'system',
                      content: 'Error de conexión. Por favor, intenta de nuevo.',
                      timestamp: new Date()
                    }
                  ]);
                  
                  // 🧠 P0 ASSISTANTSTATE: Error en voz → idle (desbloquear)
                  setAssistantState('idle');
                  console.log('🧠 [ASSISTANTSTATE][VOICE] Error al procesar voz → idle (controles desbloqueados)');
                })
                .finally(() => {
                  setIsSending(false);
                });
              }}
              disabled={isInputDisabled}
              placeholder={
                sessionPhase === 'onboarding' 
                  ? '🔒 Confirma onboarding' 
                  : sessionStatus === 'paused' 
                  ? '⏸️ Pausada' 
                  : sessionStatus === 'completed' 
                  ? '🛑 Finalizada'
                  : assistantState === 'thinking'
                  ? 'Pensando…'
                  : assistantAudioState === 'playing'
                  ? 'Reproduciendo respuesta…'
                  : 'Escribe tu mensaje...'
              }
              isSending={isSending}
              assistantAudioState={assistantAudioState}
              assistantState={assistantState}
              onStopAudio={stopAudioPlayback}
              voiceOptions={{
                minDuration: 700,
                maxDuration: 60000,
                sttTimeout: 8000
              }}
              onAnalyticsEvent={(eventName, data) => {
                console.log(`[VERTEX_VOICE_UX_P0][${eventName}]`, data);
              }}
            />
          </div>
        </div>
      </div>

      {/* PANEL DE METRICAS (Desktop ≥1025px - sidebar derecha fija) */}
      {isDesktop && !hideRightPanel && (
        <div className="w-80 border-l border-gray-200 bg-gray-50 overflow-y-auto min-h-0">
          <div className="p-4 space-y-4">
            {/* 🎟️ TICKET P0: Visualización de estado Motor V4 - P0 BUG FIX */}
            {/* 🎟️ P0 BUG FIX: Calcular phaseConfig con datos del backend */}
            {(() => {
              const currentPhaseData = agent?.stateMachine?.phases?.find(
                p => p.phaseId === currentPhase || p.id === currentPhase
              );
              
              // 🎟️ P0 BUG FIX: Priorizar datos del backend sobre agent local
              const phaseConfig = {
                // Del backend (fuente de verdad si existe)
                phaseMinTurns: sessionMetrics?.phase?.phaseMinTurns || currentPhaseData?.minTurns || 1,
                phaseMaxTurns: sessionMetrics?.phase?.phaseMaxTurns || currentPhaseData?.maxTurns || 3,
                phaseJustTransitioned: sessionMetrics?.phase?.phaseJustTransitioned || false,
                
                // Legacy support (mantener por compatibilidad)
                minTurns: sessionMetrics?.phase?.phaseMinTurns || currentPhaseData?.minTurns || 1,
                maxTurns: sessionMetrics?.phase?.phaseMaxTurns || currentPhaseData?.maxTurns || 3
              };
              
              return (
                <SessionStatusBadge
                  sessionStatus={sessionStatusAfter}
                  dataSource={{
                    sessionStatusAfter,
                    sessionStatusBefore: sessionStatus,
                    status: session?.status,
                    aiTurnCount: sessionMetrics?.aiTurnCount || sessionMetrics?.completedTurns || 0
                  }}
                  moderatorAlert={moderatorAlert}
                  currentPhase={currentPhase}
                  currentPhaseDisplayName={currentPhaseDisplayName}
                  phaseConfig={phaseConfig}
                  turnsInPhase={turnsInPhase}
                  maxTurns={sessionMetrics?.maxTurns || 0}
                  completedTurns={sessionMetrics?.completedTurns || 0}
                />
              );
            })()}
            
            {/* 🎟️ V4-P0-007: MetricsPanel solo visible para admin */}
            {isAdmin && (
              <MetricsPanel 
                sessionMetrics={sessionMetrics}
                currentPhase={currentPhase}
                currentPhaseDisplayName={sessionMetrics?.currentPhaseDisplayName}
                phaseHistory={phaseHistory}
                agentPhases={agent?.stateMachine?.phases || []}
                agentId={agent?.agentId}
                currentSessionId={session?.sessionId}
                scenarioInfo={scenarioInfo}
              />
            )}
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMACIÓN PARA SALIR */}
      {showExitConfirmation && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto"
          style={{ WebkitBackdropFilter: 'blur(4px)' }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full min-[768px]:max-w-[90vw] min-[768px]:w-[80vw] min-[768px]:max-w-[760px] min-[768px]:p-8 min-[768px]:mx-auto p-6 relative animate-in fade-in zoom-in-95 duration-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                ¿Finalizar sesión?
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Puedes salir sin generar evaluación, o confirmar para generar tu evaluación final.
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <span className="text-lg">⚠️</span>
                <p className="text-xs text-amber-800 font-medium leading-snug">
                  Esta acción no se puede deshacer. No podrás continuar esta conversación más tarde.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col gap-3">
              {!canOfferEvaluation && onViewEvaluation && (
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                  <p className="text-xs text-gray-600 leading-snug">
                    Aún no hay suficiente interacción para generar evaluación.
                  </p>
                </div>
              )}

              {canOfferEvaluation && (
                <button
                  onClick={async () => {
                    setIsExiting(true);
                    try {
                      setShowExitConfirmation(false);
                      const handler = onGenerateEvaluation || onViewEvaluation;
                      if (handler) {
                        await handler(session.sessionId);
                      }
                    } finally {
                      setIsExiting(false);
                    }
                  }}
                  disabled={isExiting}
                  className="w-full py-3.5 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-all shadow-lg shadow-green-200 flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  {isExiting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generando evaluación...
                    </>
                  ) : (
                    'Generar evaluación y finalizar'
                  )}
                </button>
              )}

              <button
                onClick={async () => {
                  await exitSession();
                }}
                disabled={isExiting}
                className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed transition-all shadow-lg shadow-red-200 flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                {isExiting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saliendo...
                  </>
                ) : (
                  'Salir sin evaluación'
                )}
              </button>
              
              <button
                onClick={() => setShowExitConfirmation(false)}
                disabled={isExiting}
                className="w-full py-3.5 bg-white text-gray-700 font-medium rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors active:scale-[0.98]"
              >
                Cancelar y volver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Build version stamp (forces new bundle hash) - PHASE-DISPLAYNAME-V4
const BUILD_VERSION = '20260110-1000-PHASE-DISPLAYNAME-V4';

export default VertexChatWindow;
